import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useContext, useRef } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import {
	Copy,
	Loader2,
	Cake,
	ShieldCheck,
	Palette,
	SlidersHorizontal,
	KeyRound,
	Plug,
	Upload,
	Check,
	CalendarDays,
	AlertTriangle,
	ChevronDown,
	ChevronRight,
	DatabaseBackup,
	Share2,
	MessageSquareText,
	Mail,
	Server,
	Users,
	UserRound,
	Building2,
	Megaphone,
	BellRing,
	Eye,
	SendHorizonal,
	Wrench,
	Rocket,
	Settings2,
	Percent,
	ShieldAlert,
	Info,
} from "lucide-react";

import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import BackupRestore from "../../components/BackupRestore";
import MetaSettings from "../../components/MetaSettings";
import { AuthContext } from "../../context/Auth/AuthContext";
// WhiteLabelProvider global: recarrega favicon, nome, cor, login em TODAS as telas
// (layout/sidebar, login, auth layout). Após salvar aparência no Settings, chamamos
// .recarregar() para atualizar em tempo real a aba do navegador sem F5.
import { useWhiteLabel } from "../../context/WhiteLabel";

// Cada aba abre com uma linha dizendo para que ela serve — quem chega aqui
// não deveria ter de adivinhar pelo nome da aba.
const SECTIONS_TODAS = [
	{
		id: "geral",
		label: "Geral",
		icon: SlidersHorizontal,
		descricao:
			"Quem pode entrar no sistema e como as pessoas criam a conta delas.",
	},
	{
		id: "aparencia",
		label: "Aparência",
		icon: Palette,
		descricao:
			"O nome, a cor e as imagens que sua equipe e seus clientes veem nas telas.",
	},
	{
		id: "saas-email",
		label: "SaaS · E-mail do sistema",
		icon: Mail,
		descricao:
			"Remetente e servidor SMTP usados para envios automáticos (ex.: redefinir senha). Apenas para o dono do SaaS.",
		soSuperAdmin: true,
	},
	{
		id: "automacoes",
		label: "Automações",
		icon: Cake,
		descricao:
			"Mensagens que o sistema envia sozinho, sem ninguém precisar digitar.",
	},
	{
		id: "seguranca",
		label: "Segurança",
		icon: ShieldCheck,
		descricao:
			"Proteção extra para a sua conta na hora de entrar no sistema.",
	},
	{
		id: "api",
		label: "API",
		icon: KeyRound,
		descricao:
			"A senha que outro programa usa para conversar com o sistema no seu lugar.",
	},
	{
		id: "meta",
		label: "Meta",
		icon: Share2,
		descricao:
			"As credenciais do app do Facebook que ligam o Messenger, o Instagram e o WhatsApp oficial ao sistema.",
	},
	{
		id: "backup",
		label: "Backup",
		icon: DatabaseBackup,
		descricao:
			"Levar a configuração da empresa para um arquivo, e trazer de volta em outra instalação ou depois de uma reinstalação.",
	},
];
const SECTIONS = SECTIONS_TODAS;

// Bloco de aviso: usado onde a configuração tem um efeito que costuma
// pegar as pessoas de surpresa.
const Aviso = ({ children }) => (
	<div className="flex gap-2 rounded-lg bg-muted p-3 text-xs leading-relaxed">
		<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
		<div>{children}</div>
	</div>
);

const SettingsCard = ({
	icon: Icon,
	title,
	description,
	action,
	footer,
	children,
}) => (
	<div className="overflow-hidden rounded-xl border bg-card">
		<div className="flex items-start justify-between gap-4 border-b px-5 py-4">
			<div className="flex min-w-0 items-start gap-3">
				{Icon ? (
					<span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
						<Icon className="h-4 w-4" />
					</span>
				) : null}
				<div className="min-w-0">
					<h2 className="text-sm font-semibold tracking-tight">{title}</h2>
					{description ? (
						<p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
					) : null}
				</div>
			</div>
			{action ? <div className="shrink-0">{action}</div> : null}
		</div>
		<div className="px-5 py-5">{children}</div>
		{footer ? (
			<div className="flex justify-end border-t bg-muted/20 px-5 py-3">{footer}</div>
		) : null}
	</div>
);

const Settings = () => {
	const navigate = useNavigate();
	const { user } = useContext(AuthContext);
	const { whatsApps } = useWhatsApps();
	// Usado para recarregar em TEMPO REAL a aparência GLOBAL (nome app, cor
	// primária e FAVICON DA ABA) após salvar aqui no Settings.
	const { recarregar: recarregarWhiteLabel } = useWhiteLabel();

	const [activeSection, setActiveSection] = useState("geral");

	const [settings, setSettings] = useState([]);
	const [googleCalendar, setGoogleCalendar] = useState({
		clientId: "",
		clientSecret: "",
		refreshToken: "",
		defaultCalendarId: "primary",
		pronto: false,
	});
	const [novoGoogleCalendarSecret, setNovoGoogleCalendarSecret] = useState("");
	const [novoGoogleCalendarRefresh, setNovoGoogleCalendarRefresh] = useState("");
	const [savingGoogleCalendar, setSavingGoogleCalendar] = useState(false);
	const [tutorialGoogleCalendar, setTutorialGoogleCalendar] = useState(false);

	// White-label
	const [whiteLabel, setWhiteLabel] = useState({
		appName: "",
		primaryColor: "#7367F0",
		logoUrl: null,
		backgroundUrl: null,
		loginTitle: "",
		loginSubtitle: "",
		loginDescription: "",
		loginFeatures: [
			{ titulo: "", detalhe: "" },
			{ titulo: "", detalhe: "" },
			{ titulo: "", detalhe: "" },
		],
		loginHeroImageUrl: null,
		loginLogoUrl: null,
		faviconUrl: null,

		smtpFromName: "",
		smtpFromEmail: "",
		smtpHost: "",
		smtpPort: 587,
		smtpSecure: false,
		smtpUser: "",
		smtpPass: "",
		smtpPassIsSet: false,

		infoSmtpFromName: "",
		infoSmtpFromEmail: "",
		infoSmtpHost: "",
		infoSmtpPort: 587,
		infoSmtpSecure: false,
		infoSmtpUser: "",
		infoSmtpPass: "",
		infoSmtpPassIsSet: false,
	});
	const [savingWhiteLabel, setSavingWhiteLabel] = useState(false);
	const [testandoSmtp, setTestandoSmtp] = useState(false);
	const [testandoInfoSmtp, setTestandoInfoSmtp] = useState(false);
	const [modalComunicacaoAberto, setModalComunicacaoAberto] = useState(false);
	const [campanha, setCampanha] = useState({
		segments: [],
		emailsExtras: "",
		subject: "",
		message: "",
		confirmado: false,
	});
	const [msgRef, setMsgRef] = useState(null);
	const [campanhaErros, setCampanhaErros] = useState({
		emailsExtras: null,
	});
	const [modeloPendente, setModeloPendente] = useState(null);
	const [enviandoCampanha, setEnviandoCampanha] = useState(false);
	const [gerandoPreview, setGerandoPreview] = useState(false);
	const [preview, setPreview] = useState(null);
	const logoInputRef = useRef(null);
	const backgroundInputRef = useRef(null);
	const loginHeroInputRef = useRef(null);
	const loginLogoInputRef = useRef(null);
	const faviconInputRef = useRef(null);

	// Birthdays
	const [birthdaySettings, setBirthdaySettings] = useState({
		isEnabled: false,
		messageTemplate: "",
		sendHour: 9,
		whatsappId: null,
	});
	const [savingBirthday, setSavingBirthday] = useState(false);
	const [todayBirthdays, setTodayBirthdays] = useState([]);
	const [listaAniversarioAberta, setListaAniversarioAberta] = useState(false);

	// 2FA
	const [twoFactorEnabled, setTwoFactorEnabled] = useState(
		!!user?.twoFactorEnabled
	);
	const [twoFactorSetup, setTwoFactorSetup] = useState(null);
	const [twoFactorToken, setTwoFactorToken] = useState("");
	const [loading2FA, setLoading2FA] = useState(false);
	// Pedida para desligar o segundo fator — o backend recusa sem ela.
	const [senhaParaDesligar2FA, setSenhaParaDesligar2FA] = useState("");

	useEffect(() => {
		setTwoFactorEnabled(!!user?.twoFactorEnabled);
	}, [user]);

	// ── VERBATIM usuário 25/09/2026: ────────────────────────────────────
	// "REMOVER PERSONALIZAÇÃO DE CLIENTE SAAS em configurações isso é apenas
	//  para o donoo do saas."
	// ──────────────────────────────────────────────────────────────────────
	// Regra IDÊNTICA à da MainListItems L501 (mesmo critério backend isSuperAdmin):
	//   • user.profile === "admin"
	//   • Number(user.companyId) === 1 (EMPRESA_OPERADORA = dono do SaaS).
	//
	// Comportamento:
	// • SE É superadmin → aba "Aparência" aparece NORMALMENTE no nav.
	// • SE NÃO É superadmin →
	//     (1) aba de Aparência é REMOVIDA do array SECTIONS renderizado no nav.
	//     (2) se o usuário entrou por um link antigo / hash que definia
	//         activeSection === "aparencia", redireciona automaticamente para
	//         "geral" (evita tela em branco ou conteúdo não autorizado).
	const ehSuperAdmin = Boolean(
		user &&
			String(user.profile || "").toLowerCase() === "admin" &&
			Number(user.companyId || 0) === 1,
	);
	const sectionsVisiveis = ehSuperAdmin
		? SECTIONS_TODAS.filter((s) => !s.soSuperAdmin || ehSuperAdmin)
		: SECTIONS_TODAS.filter((s) => s.id !== "aparencia" && !s.soSuperAdmin);

	useEffect(() => {
		// Auto-correção activeSection se: não é superadmin e estava em aparência.
		if (!ehSuperAdmin && activeSection === "aparencia") {
			setActiveSection("geral");
		}
		// Também: se activeSection é uma seção que NÃO existe mais em sectionsVisiveis,
		// força para a 1ª disponível (ex: futura seção nova superadmin, etc.).
		if (!sectionsVisiveis.some((s) => s.id === activeSection) && sectionsVisiveis.length > 0) {
			const primeira = sectionsVisiveis[0].id;
			if (primeira !== activeSection) setActiveSection(primeira);
		}
	}, [ehSuperAdmin, activeSection, sectionsVisiveis]);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const carregarGoogleCalendar = async () => {
			try {
				const { data } = await api.get("/settings/google-calendar");
				setGoogleCalendar({
					clientId: data?.clientId || "",
					clientSecret: data?.clientSecret || "",
					refreshToken: data?.refreshToken || "",
					defaultCalendarId: data?.defaultCalendarId || "primary",
					pronto: Boolean(data?.pronto),
				});
			} catch (err) {
				toastError(err);
			}
		};

		carregarGoogleCalendar();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/settings/white-label");
				const features = Array.isArray(data.loginFeatures) && data.loginFeatures.length > 0
					? data.loginFeatures
					: [
						{ titulo: "", detalhe: "" },
						{ titulo: "", detalhe: "" },
						{ titulo: "", detalhe: "" },
					  ];
				while (features.length < 3) {
					features.push({ titulo: "", detalhe: "" });
				}
				setWhiteLabel({
					appName: data.appName || "",
					primaryColor: data.primaryColor || "#7367F0",
					logoUrl: data.logoUrl || null,
					backgroundUrl: data.backgroundUrl || null,
					loginTitle: data.loginTitle || "",
					loginSubtitle: data.loginSubtitle || "",
					loginDescription: data.loginDescription || "",
					loginFeatures: features.slice(0, 3),
					loginHeroImageUrl: data.loginHeroImageUrl || null,
					loginLogoUrl: data.loginLogoUrl || null,
					faviconUrl: data.faviconUrl || null,

					smtpFromName: data.smtpFromName || "",
					smtpFromEmail: data.smtpFromEmail || "",
					smtpHost: data.smtpHost || "",
					smtpPort: data.smtpPort != null ? Number(data.smtpPort) : 587,
					smtpSecure: Boolean(data.smtpSecure),
					smtpUser: data.smtpUser || "",
					smtpPass: "",
					smtpPassIsSet: Boolean(data.smtpPassIsSet),

					infoSmtpFromName: data.infoSmtpFromName || "",
					infoSmtpFromEmail: data.infoSmtpFromEmail || "",
					infoSmtpHost: data.infoSmtpHost || "",
					infoSmtpPort: data.infoSmtpPort != null ? Number(data.infoSmtpPort) : 587,
					infoSmtpSecure: Boolean(data.infoSmtpSecure),
					infoSmtpUser: data.infoSmtpUser || "",
					infoSmtpPass: "",
					infoSmtpPassIsSet: Boolean(data.infoSmtpPassIsSet),
				});
			} catch (err) {
				toastError(err);
			}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const { data } = await api.get("/birthdays/settings");
				setBirthdaySettings({
					isEnabled: !!data.isEnabled,
					messageTemplate: data.messageTemplate || "",
					sendHour: data.sendHour ?? 9,
					whatsappId: data.whatsappId || null,
				});
			} catch (err) {
				toastError(err);
			}
		})();

		(async () => {
			try {
				const { data } = await api.get("/birthdays/today");
				setTodayBirthdays(Array.isArray(data) ? data : []);
			} catch (err) {
				toastError(err);
			}
		})();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		const onSettings = (data) => {
			if (data.action === "update" && data.setting && data.setting.key) {
				setSettings((prevState) => {
					const settingIndex = prevState.findIndex((s) => s.key === data.setting.key);
					if (settingIndex === -1) {
						return [...prevState, data.setting];
					}
					const aux = [...prevState];
					aux[settingIndex] = { ...aux[settingIndex], ...data.setting };
					return aux;
				});
			}
		};

		socket.on("settings", onSettings);

		return () => {
			try { socket.off("settings", onSettings); } catch (_) {}
		};
	}, []);

	const handleChangeSetting = async (key, value) => {
		try {
			const { data: settingSalvo } = await api.put(`/settings/${key}`, { value });
			setSettings((prev) => {
				const idx = prev.findIndex((s) => s.key === key);
				if (idx === -1) {
					return [...prev, settingSalvo];
				}
				const aux = [...prev];
				aux[idx] = { ...aux[idx], ...settingSalvo, value: settingSalvo.value ?? value };
				return aux;
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = (key) => {
		const setting = settings.find((s) => s.key === key);
		return setting ? setting.value : "";
	};

	const handleCopyToken = () => {
		navigator.clipboard.writeText(getSettingValue("userApiToken"));
		toast.success(i18n.t("settings.success"));
	};

	const handleSaveGoogleCalendar = async () => {
		setSavingGoogleCalendar(true);
		try {
			const payload = {
				clientId: googleCalendar.clientId || "",
				defaultCalendarId: googleCalendar.defaultCalendarId || "primary",
			};

			if (novoGoogleCalendarSecret.trim()) {
				payload.clientSecret = novoGoogleCalendarSecret.trim();
			}

			if (novoGoogleCalendarRefresh.trim()) {
				payload.refreshToken = novoGoogleCalendarRefresh.trim();
			}

			const { data } = await api.put("/settings/google-calendar", payload);

			setGoogleCalendar({
				clientId: data?.clientId || "",
				clientSecret: data?.clientSecret || "",
				refreshToken: data?.refreshToken || "",
				defaultCalendarId: data?.defaultCalendarId || "primary",
				pronto: Boolean(data?.pronto),
			});
			setNovoGoogleCalendarSecret("");
			setNovoGoogleCalendarRefresh("");
			toast.success("Google Calendar salvo.");
		} catch (err) {
			toastError(err);
		}
		setSavingGoogleCalendar(false);
	};

	// ----- White-label -----

	const handleSaveWhiteLabel = async () => {
		setSavingWhiteLabel(true);
		try {
			const featuresLimpas = (whiteLabel.loginFeatures || [])
				.slice(0, 3)
				.map((f) => ({
					titulo: (f?.titulo || "").trim(),
					detalhe: (f?.detalhe || "").trim(),
				}));
			const smtpPayload = {
				smtpFromName: whiteLabel.smtpFromName,
				smtpFromEmail: whiteLabel.smtpFromEmail,
				smtpHost: whiteLabel.smtpHost,
				smtpPort: whiteLabel.smtpPort,
				smtpSecure: whiteLabel.smtpSecure,
				smtpUser: whiteLabel.smtpUser,
			};
			if (whiteLabel.smtpPass && whiteLabel.smtpPass.trim().length > 0) {
				smtpPayload.smtpPass = whiteLabel.smtpPass;
			}
			const infoSmtpPayload = {
				infoSmtpFromName: whiteLabel.infoSmtpFromName,
				infoSmtpFromEmail: whiteLabel.infoSmtpFromEmail,
				infoSmtpHost: whiteLabel.infoSmtpHost,
				infoSmtpPort: whiteLabel.infoSmtpPort,
				infoSmtpSecure: whiteLabel.infoSmtpSecure,
				infoSmtpUser: whiteLabel.infoSmtpUser,
			};
			if (whiteLabel.infoSmtpPass && whiteLabel.infoSmtpPass.trim().length > 0) {
				infoSmtpPayload.infoSmtpPass = whiteLabel.infoSmtpPass;
			}
			const { data } = await api.put("/settings/white-label", {
				appName: whiteLabel.appName,
				primaryColor: whiteLabel.primaryColor,
				logoUrl: whiteLabel.logoUrl,
				backgroundUrl: whiteLabel.backgroundUrl,
				loginTitle: whiteLabel.loginTitle,
				loginSubtitle: whiteLabel.loginSubtitle,
				loginDescription: whiteLabel.loginDescription,
				loginFeatures: featuresLimpas,
				loginHeroImageUrl: whiteLabel.loginHeroImageUrl,
				loginLogoUrl: whiteLabel.loginLogoUrl,
				faviconUrl: whiteLabel.faviconUrl,

				...smtpPayload,
				...infoSmtpPayload,
			});
			const features = Array.isArray(data.loginFeatures) && data.loginFeatures.length > 0
				? data.loginFeatures
				: [
					{ titulo: "", detalhe: "" },
					{ titulo: "", detalhe: "" },
					{ titulo: "", detalhe: "" },
				  ];
			while (features.length < 3) features.push({ titulo: "", detalhe: "" });
			setWhiteLabel({
				appName: data.appName || "",
				primaryColor: data.primaryColor || "#7367F0",
				logoUrl: data.logoUrl || null,
				backgroundUrl: data.backgroundUrl || null,
				loginTitle: data.loginTitle || "",
				loginSubtitle: data.loginSubtitle || "",
				loginDescription: data.loginDescription || "",
				loginFeatures: features.slice(0, 3),
				loginHeroImageUrl: data.loginHeroImageUrl || null,
				loginLogoUrl: data.loginLogoUrl || null,
				faviconUrl: data.faviconUrl || null,

				smtpFromName: data.smtpFromName || "",
				smtpFromEmail: data.smtpFromEmail || "",
				smtpHost: data.smtpHost || "",
				smtpPort: data.smtpPort != null ? Number(data.smtpPort) : 587,
				smtpSecure: Boolean(data.smtpSecure),
				smtpUser: data.smtpUser || "",
				smtpPass: "",
				smtpPassIsSet: Boolean(data.smtpPassIsSet),

				infoSmtpFromName: data.infoSmtpFromName || "",
				infoSmtpFromEmail: data.infoSmtpFromEmail || "",
				infoSmtpHost: data.infoSmtpHost || "",
				infoSmtpPort: data.infoSmtpPort != null ? Number(data.infoSmtpPort) : 587,
				infoSmtpSecure: Boolean(data.infoSmtpSecure),
				infoSmtpUser: data.infoSmtpUser || "",
				infoSmtpPass: "",
				infoSmtpPassIsSet: Boolean(data.infoSmtpPassIsSet),
			});
			// VERBATIM: "Carregar favicon precisa funcionar porque nõa está mudando"
			// Após salvar, REFRESH do contexto WhiteLabel GLOBAL (useWhiteLabel).
			// Isto re-aplica favicon no <link rel=icon> do head, cor primária CSS e
			// nome no document.title — deixa o favicon mudar em tempo real, sem F5.
			try {
				await Promise.resolve(recarregarWhiteLabel && recarregarWhiteLabel());
			} catch (_) {
				/* Apenas aparência: se falhar por alguma razão (ex internet) não
				   deve quebrar o resto do save do whitelabel. */
			}
			toast.success("Aparência atualizada com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setSavingWhiteLabel(false);
	};

	const handleTestarSmtp = async () => {
		setTestandoSmtp(true);
		try {
			const { data } = await api.post("/settings/white-label/test-email", {}, { timeout: 90_000 });
			if (data.ok) {
				const detalhes = [
					data.host ? `Servidor: ${data.host}:${data.port}` : null,
					data.user ? `Usuário: ${data.user}` : null,
					`Origem: ${data.origemConfig === "settings" ? "Painel SaaS" : "Variáveis de ambiente"}`,
					typeof data.tempoMs === "number" ? `Tempo: ${data.tempoMs} ms` : null,
					Array.isArray(data.accepted) && data.accepted.length > 0
						? `Aceitos: ${data.accepted.join(", ")}`
						: null,
					Array.isArray(data.rejected) && data.rejected.length > 0
						? `Rejeitados: ${data.rejected.join(", ")}`
						: null,
					data.messageId ? `ID: ${String(data.messageId).slice(0, 60)}` : null,
				].filter(Boolean).join(" · ");
				const aviso = typeof data.avisoDestinatarioFake === "string" && data.avisoDestinatarioFake.trim().length > 0
					? data.avisoDestinatarioFake
					: null;
				toast.success(
					<div>
						<div className="font-semibold">E-mail de teste enviado!</div>
						<div className="text-xs opacity-90 mt-0.5">
							Confira sua caixa de entrada ({data.to}).
						</div>
						{detalhes && (
							<div className="text-[10px] opacity-80 mt-1">{detalhes}</div>
						)}
						{data.smtpResponse && (
							<div className="text-[10px] opacity-75 mt-1 break-words whitespace-pre-line">
								SMTP: {String(data.smtpResponse).slice(0, 200)}
							</div>
						)}
						{aviso && (
							<div className="mt-2 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-700 dark:text-amber-200 px-2.5 py-1.5 text-[11px] leading-snug">
								{aviso}
							</div>
						)}
					</div>
				);
			} else {
				const detalhes = [
					data.host ? `Servidor: ${data.host}:${data.port ?? "?"}` : null,
					Array.isArray(data.accepted) && data.accepted.length > 0
						? `Aceitos: ${data.accepted.join(", ")}`
						: null,
					Array.isArray(data.rejected) && data.rejected.length > 0
						? `Rejeitados: ${data.rejected.join(", ")}`
						: null,
					data.verifyOk === false
						? `Verify SMTP: FALHOU`
						: data.verifyOk === true
							? `Verify SMTP: OK`
							: null,
					typeof data.tempoMs === "number" ? `Tempo: ${data.tempoMs} ms` : null,
					data.origemConfig
						? `Origem: ${data.origemConfig === "settings" ? "Painel SaaS" : "Variáveis de ambiente"}`
						: null,
				].filter(Boolean).join(" · ");
				toast.error(
					<div>
						<div className="font-semibold">Falha ao enviar e-mail de teste</div>
						<div className="text-xs opacity-90 mt-0.5 break-words whitespace-pre-line">
							{data.erro || "Verifique host, porta, usuário e senha."}
						</div>
						{data.verifyErro && (
							<div className="text-[10px] opacity-80 mt-1 break-words whitespace-pre-line">
								Erro verify(): {String(data.verifyErro).slice(0, 240)}
							</div>
						)}
						{detalhes && (
							<div className="text-[10px] opacity-80 mt-1">{detalhes}</div>
						)}
						{data.smtpResponse && (
							<div className="text-[10px] opacity-75 mt-1 break-words whitespace-pre-line">
								SMTP: {String(data.smtpResponse).slice(0, 200)}
							</div>
						)}
						{typeof data.avisoDestinatarioFake === "string" && data.avisoDestinatarioFake.trim().length > 0 && (
							<div className="mt-2 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-700 dark:text-amber-200 px-2.5 py-1.5 text-[11px] leading-snug">
								{data.avisoDestinatarioFake}
							</div>
						)}
					</div>
				);
			}
		} catch (err) {
			const backendErro = err?.response?.data?.erro;
			const backendHost = err?.response?.data?.host;
			const backendPort = err?.response?.data?.port;
			const backendOrigem = err?.response?.data?.origemConfig;
			const backendVerifyErro = err?.response?.data?.verifyErro;
			const backendAccepted = Array.isArray(err?.response?.data?.accepted) ? err.response.data.accepted : null;
			const backendRejected = Array.isArray(err?.response?.data?.rejected) ? err.response.data.rejected : null;
			const backendSmtpResp = err?.response?.data?.smtpResponse;
			const backendMsgId = err?.response?.data?.messageId;
			const backendTempo = err?.response?.data?.tempoMs;
			const backendVerifyOk = err?.response?.data?.verifyOk;
			const backendAvisoFake = err?.response?.data?.avisoDestinatarioFake;
			if (typeof backendErro === "string" && backendErro.trim().length > 0) {
				const detalhes = [
					backendHost ? `Servidor: ${backendHost}:${backendPort ?? "?"}` : null,
					backendAccepted && backendAccepted.length > 0 ? `Aceitos: ${backendAccepted.join(", ")}` : null,
					backendRejected && backendRejected.length > 0 ? `Rejeitados: ${backendRejected.join(", ")}` : null,
					backendVerifyOk === false
						? `Verify SMTP: FALHOU`
						: backendVerifyOk === true
							? `Verify SMTP: OK`
							: null,
					typeof backendTempo === "number" ? `Tempo: ${backendTempo} ms` : null,
					backendOrigem
						? `Origem: ${backendOrigem === "settings" ? "Painel SaaS" : "Variáveis de ambiente"}`
						: null,
					backendMsgId ? `ID: ${String(backendMsgId).slice(0, 60)}` : null,
				].filter(Boolean).join(" · ");
				toast.error(
					<div>
						<div className="font-semibold">Falha ao enviar e-mail de teste</div>
						<div className="text-xs opacity-90 mt-0.5 break-words whitespace-pre-line">
							{backendErro}
						</div>
						{backendVerifyErro && (
							<div className="text-[10px] opacity-80 mt-1 break-words whitespace-pre-line">
								Erro verify(): {String(backendVerifyErro).slice(0, 240)}
							</div>
						)}
						{detalhes && (
							<div className="text-[10px] opacity-80 mt-1">{detalhes}</div>
						)}
						{backendSmtpResp && (
							<div className="text-[10px] opacity-75 mt-1 break-words whitespace-pre-line">
								SMTP: {String(backendSmtpResp).slice(0, 200)}
							</div>
						)}
						{typeof backendAvisoFake === "string" && backendAvisoFake.trim().length > 0 && (
							<div className="mt-2 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-700 dark:text-amber-200 px-2.5 py-1.5 text-[11px] leading-snug">
								{backendAvisoFake}
							</div>
						)}
					</div>
				);
			} else {
				toastError(err);
			}
		} finally {
			setTestandoSmtp(false);
		}
	};

	const handleTestarInfoSmtp = async () => {
		setTestandoInfoSmtp(true);
		try {
			const { data } = await api.post("/settings/white-label/test-email-info", {}, { timeout: 90_000 });
			if (data.ok) {
				const detalhes = [
					data.host ? `Servidor: ${data.host}:${data.port}` : null,
					data.user ? `Usuário: ${data.user}` : null,
					`Origem: ${data.origemConfig === "settings" ? "Painel SaaS" : "Variáveis de ambiente"}`,
					typeof data.tempoMs === "number" ? `Tempo: ${data.tempoMs} ms` : null,
					Array.isArray(data.accepted) && data.accepted.length > 0
						? `Aceitos: ${data.accepted.join(", ")}`
						: null,
					Array.isArray(data.rejected) && data.rejected.length > 0
						? `Rejeitados: ${data.rejected.join(", ")}`
						: null,
					data.messageId ? `ID: ${String(data.messageId).slice(0, 60)}` : null,
				].filter(Boolean).join(" · ");
				const aviso = typeof data.avisoDestinatarioFake === "string" && data.avisoDestinatarioFake.trim().length > 0
					? data.avisoDestinatarioFake
					: null;
				toast.success(
					<div>
						<div className="font-semibold">E-mail de teste (Informativo) enviado!</div>
						<div className="text-xs opacity-90 mt-0.5">
							Confira sua caixa de entrada ({data.to}). O From vem do card E-mail Informações.
						</div>
						{detalhes && (
							<div className="text-[10px] opacity-80 mt-1">{detalhes}</div>
						)}
						{data.smtpResponse && (
							<div className="text-[10px] opacity-75 mt-1 break-words whitespace-pre-line">
								SMTP: {String(data.smtpResponse).slice(0, 200)}
							</div>
						)}
						{aviso && (
							<div className="mt-2 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-700 dark:text-amber-200 px-2.5 py-1.5 text-[11px] leading-snug">
								{aviso}
							</div>
						)}
					</div>
				);
			} else {
				const detalhes = [
					data.host ? `Servidor: ${data.host}:${data.port ?? "?"}` : null,
					Array.isArray(data.accepted) && data.accepted.length > 0
						? `Aceitos: ${data.accepted.join(", ")}`
						: null,
					Array.isArray(data.rejected) && data.rejected.length > 0
						? `Rejeitados: ${data.rejected.join(", ")}`
						: null,
					data.verifyOk === false
						? `Verify SMTP: FALHOU`
						: data.verifyOk === true
							? `Verify SMTP: OK`
							: null,
					typeof data.tempoMs === "number" ? `Tempo: ${data.tempoMs} ms` : null,
					data.origemConfig
						? `Origem: ${data.origemConfig === "settings" ? "Painel SaaS" : "Variáveis de ambiente"}`
						: null,
				].filter(Boolean).join(" · ");
				toast.error(
					<div>
						<div className="font-semibold">Falha no teste E-mail Informações</div>
						<div className="text-xs opacity-90 mt-0.5 break-words whitespace-pre-line">
							{data.erro || "Verifique host, porta, usuário e senha do SMTP informativo."}
						</div>
						{data.verifyErro && (
							<div className="text-[10px] opacity-80 mt-1 break-words whitespace-pre-line">
								Erro verify(): {String(data.verifyErro).slice(0, 240)}
							</div>
						)}
						{detalhes && (
							<div className="text-[10px] opacity-80 mt-1">{detalhes}</div>
						)}
						{data.smtpResponse && (
							<div className="text-[10px] opacity-75 mt-1 break-words whitespace-pre-line">
								SMTP: {String(data.smtpResponse).slice(0, 200)}
							</div>
						)}
					</div>
				);
			}
		} catch (err) {
			const backendErro = err?.response?.data?.erro;
			const backendHost = err?.response?.data?.host;
			const backendPort = err?.response?.data?.port;
			const backendVerifyErro = err?.response?.data?.verifyErro;
			const backendAccepted = Array.isArray(err?.response?.data?.accepted) ? err.response.data.accepted : null;
			const backendRejected = Array.isArray(err?.response?.data?.rejected) ? err.response.data.rejected : null;
			const backendSmtpResp = err?.response?.data?.smtpResponse;
			const backendTempo = err?.response?.data?.tempoMs;
			const backendVerifyOk = err?.response?.data?.verifyOk;
			if (typeof backendErro === "string" && backendErro.trim().length > 0) {
				const detalhes = [
					backendHost ? `Servidor: ${backendHost}:${backendPort ?? "?"}` : null,
					backendAccepted && backendAccepted.length > 0 ? `Aceitos: ${backendAccepted.join(", ")}` : null,
					backendRejected && backendRejected.length > 0 ? `Rejeitados: ${backendRejected.join(", ")}` : null,
					backendVerifyOk === false
						? `Verify SMTP: FALHOU`
						: backendVerifyOk === true
							? `Verify SMTP: OK`
							: null,
					typeof backendTempo === "number" ? `Tempo: ${backendTempo} ms` : null,
				].filter(Boolean).join(" · ");
				toast.error(
					<div>
						<div className="font-semibold">Falha no teste E-mail Informações</div>
						<div className="text-xs opacity-90 mt-0.5 break-words whitespace-pre-line">
							{backendErro}
						</div>
						{backendVerifyErro && (
							<div className="text-[10px] opacity-80 mt-1 break-words whitespace-pre-line">
								Erro verify(): {String(backendVerifyErro).slice(0, 240)}
							</div>
						)}
						{detalhes && (
							<div className="text-[10px] opacity-80 mt-1">{detalhes}</div>
						)}
						{backendSmtpResp && (
							<div className="text-[10px] opacity-75 mt-1 break-words whitespace-pre-line">
								SMTP: {String(backendSmtpResp).slice(0, 200)}
							</div>
						)}
					</div>
				);
			} else {
				toastError(err);
			}
		} finally {
			setTestandoInfoSmtp(false);
		}
	};

	const SEGMENTOS_CAMPANHA = [
		{
			id: "adminEmpresas",
			titulo: "Admins das empresas assinantes",
			descricao: "Responsáveis admin/owner das empresas clientes do SaaS (exclui empresa 1).",
			icone: Users,
		},
		{
			id: "todosUsuarios",
			titulo: "Todos os usuários do sistema",
			descricao: "Qualquer usuário de qualquer empresa cliente (não é só admins).",
			icone: Megaphone,
		},
		{
			id: "todosContatos",
			titulo: "Todos os contatos (CRM)",
			descricao: "Qualquer contato salvo na base que tenha e-mail.",
			icone: BellRing,
		},
		{
			id: "contatosAtivos90d",
			titulo: "Contatos ativos nos últimos 90 dias",
			descricao: "Apenas contatos que abriram atendimento/ticket recentemente.",
			icone: MessageSquareText,
		},
	];

	const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const parsearEmailsExtras = (raw) => {
		if (!raw) return { lista: [], invalidos: [] };
		const str = String(raw ?? "");
		if (!str) return { lista: [], invalidos: [] };
		const partes = str
			.split(/[\s,;]+/)
			.map((s) => String(s ?? "").trim().toLowerCase())
			.filter(Boolean);
		const vistos = new Set();
		const lista = [];
		const invalidos = [];
		partes.forEach((e) => {
			if (!e || vistos.has(e)) return;
			vistos.add(e);
			if (EMAIL_REGEX.test(e)) lista.push(e);
			else invalidos.push(e);
		});
		return { lista, invalidos };
	};

	const validarEmailsExtras = () => {
		const { invalidos } = parsearEmailsExtras(campanha?.emailsExtras);
		if (invalidos.length) {
			setCampanhaErros((prev) => ({
				...prev,
				emailsExtras: `${invalidos.length} e-mail(s) inválido(s): ${invalidos.slice(0, 3).join(", ")}${invalidos.length > 3 ? "…" : ""}`,
			}));
			return false;
		}
		setCampanhaErros((prev) => ({ ...prev, emailsExtras: null }));
		return true;
	};

	const inserirTextoMensagem = (antes, depois = "", textoPlaceholder = "") => {
		const el = msgRef;
		if (!el || typeof el.value !== "string") {
			setCampanha((prev) => ({
				...prev,
				message: String(prev?.message ?? "") + String(antes ?? "") + String(textoPlaceholder ?? "") + String(depois ?? ""),
			}));
			return;
		}
		const value = el.value;
		const start = Number.isFinite(el.selectionStart) ? Math.min(Math.max(el.selectionStart, 0), value.length) : value.length;
		const end = Number.isFinite(el.selectionEnd) ? Math.min(Math.max(el.selectionEnd, start), value.length) : start;
		const selecionado = value.slice(start, end) || String(textoPlaceholder ?? "");
		const novo = value.slice(0, start) + String(antes ?? "") + selecionado + String(depois ?? "") + value.slice(end);
		setCampanha((prev) => ({ ...prev, message: novo }));
		requestAnimationFrame(() => {
			try {
				el.focus();
				const novaPos = start + String(antes ?? "").length + selecionado.length;
				el.setSelectionRange(novaPos, novaPos);
			} catch (_) { /* ignore */ }
		});
	};

	const MODELOS_COMUNICACAO = [
		{
			id: "manutencao-programada",
			titulo: "Manutenção programada",
			descricao: "Aviso de janela de manutenção com data, horário e impacto.",
			icone: Wrench,
			cor: "border-sky-500/40 hover:border-sky-500/70 hover:bg-sky-500/5",
			subject: "⚠️ Manutenção programada no {{empresa}}",
			message: `<h2>Olá {{primeiroNome}}!</h2>
<p>Queremos te avisar com antecedência sobre uma janela de manutenção programada para melhorar a estabilidade e o desempenho do <strong>{{empresa}}</strong>.</p>
<h3>Quando</h3>
<ul>
  <li><strong>Data:</strong> sábado (dia XX / XX)</li>
  <li><strong>Horário:</strong> das 00:00 às 04:00 (horário de Brasília)</li>
  <li><strong>Duração estimada:</strong> ~3h</li>
</ul>
<h3>Impacto esperado</h3>
<p>Durante este período, o sistema pode ficar <em>indisponível ou com desempenho reduzido</em> por alguns minutos. Nenhuma informação será perdida.</p>
<blockquote>Recomendamos não agendar envios em massa nem operações críticas para este horário.</blockquote>
<p>Agradecemos pela sua compreensão! Se tiver dúvidas, é só entrar em contato com o nosso time.</p>
<p>Abraços.</p>`,
		},
		{
			id: "novidade-lancamento",
			titulo: "Novidade / Lançamento",
			descricao: "Anunciar uma funcionalidade nova, módulo ou melhoria grande.",
			icone: Rocket,
			cor: "border-violet-500/40 hover:border-violet-500/70 hover:bg-violet-500/5",
			subject: "🚀 Novidade no {{empresa}}: conheça a nova funcionalidade",
			message: `<h2>E aí, {{primeiroNome}}! Temos novidade 🎉</h2>
<p>A equipe do <strong>{{empresa}}</strong> lançou uma nova funcionalidade que vai acelerar ainda mais o seu dia a dia.</p>
<h3>✨ O que mudou</h3>
<ul>
  <li><strong>Funcionalidade 1:</strong> explicação curta de como ajuda</li>
  <li><strong>Funcionalidade 2:</strong> explicação curta de como ajuda</li>
  <li><strong>Melhoria em:</strong> detalhe do que já existia e foi otimizado</li>
</ul>
<h3>Como usar</h3>
<p>Para começar, basta entrar na tela correspondente dentro do sistema e seguir o passo a passo. Se precisar, temos <a href="https://seu-link-de-ajuda.com">material de ajuda aqui</a>.</p>
<hr />
<p>Esperamos que goste! Qualquer dúvida ou sugestão, é só responder este e-mail.</p>
<p>Até mais 👋</p>`,
		},
		{
			id: "aviso-correcao",
			titulo: "Aviso de correção / atualização",
			descricao: "Comunicar bugs corrigidos, ajustes e melhorias pequenas.",
			icone: Settings2,
			cor: "border-emerald-500/40 hover:border-emerald-500/70 hover:bg-emerald-500/5",
			subject: "🛠️ Atualização aplicada no {{empresa}}",
			message: `<h2>Olá {{primeiroNome}}, acabamos de atualizar o {{empresa}}!</h2>
<p>Esta atualização traz <strong>ajustes de estabilidade</strong> e correções de bugs que estavam impactando a experiência.</p>
<h3>Correções incluídas</h3>
<ul>
  <li>Corrigido o problema X ao salvar registros</li>
  <li>Melhoria no carregamento da tela inicial</li>
  <li>Ajuste no filtro de relatórios (agora respeita corretamente o período selecionado)</li>
  <li>Otimização no envio de notificações</li>
</ul>
<h3>Você precisa fazer algo?</h3>
<p>Não. A atualização já está aplicada em todas as contas. Basta recarregar a página (F5) caso ainda não veja as mudanças.</p>
<p>Contamos com o seu feedback para continuar evoluindo. Encontrou algo fora do lugar? <strong>Reporta para a gente!</strong></p>
<p>Até a próxima atualização ✅</p>`,
		},
		{
			id: "promocao-relampago",
			titulo: "Promoção / condição especial",
			descricao: "Ofertas, condições especiais, desconto ou bônus por tempo.",
			icone: Percent,
			cor: "border-pink-500/40 hover:border-pink-500/70 hover:bg-pink-500/5",
			subject: "⚡ Promoção exclusiva para {{nome}} — só hoje!",
			message: `<h2>Opa {{primeiroNome}}! Preparamos uma condição especial pra você ⚡</h2>
<p>Pensando em agradecer por estar com a gente, preparamos um benefício exclusivo por tempo limitado.</p>
<h3>O que você ganha</h3>
<ul>
  <li><strong>-X% de desconto</strong> em [insira aqui o produto/serviço]</li>
  <li><strong>Brinde:</strong> descrição curta do brinde, se houver</li>
  <li><strong>Vigência:</strong> até XX/XX (corra!)</li>
</ul>
<blockquote>Lembre-se: quanto antes você aproveitar, mais cedo começa a usufruir.</blockquote>
<h3>Como ativar</h3>
<p>É só responder este e-mail com a palavra <strong>QUERO</strong> ou entrar em contato com o nosso time comercial que cuidamos de tudo.</p>
<p>Conta com a gente! 👊</p>`,
		},
		{
			id: "seguranca-aviso",
			titulo: "Aviso de segurança",
			descricao: "Alerta importante: senhas, 2FA, mudanças de política etc.",
			icone: ShieldAlert,
			cor: "border-amber-500/40 hover:border-amber-500/70 hover:bg-amber-500/5",
			subject: "🔐 Aviso importante de segurança do {{empresa}}",
			message: `<h2>Olá {{primeiroNome}}. Aviso rápido de segurança 🛡️</h2>
<p>Para manter a sua conta e os seus dados sempre protegidos, listamos abaixo algumas <strong>recomendações rápidas</strong>.</p>
<h3>Dicas essenciais</h3>
<ul>
  <li>Ative a <strong>autenticação em duas etapas (2FA)</strong>, se disponível</li>
  <li>Use sempre uma senha <strong>forte e exclusiva</strong> (não reutilize senhas de outros serviços)</li>
  <li>Cuidado com links recebidos por WhatsApp ou e-mail: confirme sempre a origem antes de clicar</li>
  <li>Nunca compartilhe códigos de verificação com ninguém</li>
</ul>
<blockquote>Em caso de atividade suspeita na sua conta, recomendamos alterar a senha imediatamente e notificar o suporte.</blockquote>
<h3>A sua segurança é prioridade aqui</h3>
<p>Qualquer dúvida sobre a sua conta, fale diretamente com o responsável pela sua empresa ou pelo suporte oficial do <strong>{{empresa}}</strong>.</p>
<p>Fique seguro! 💙</p>`,
		},
	];

	const aplicarModelo = (m) => {
		setCampanha((prev) => ({
			...prev,
			subject: String(m.subject || ""),
			message: String(m.message || ""),
		}));
		toast.success(`Modelo "${m.titulo}" aplicado. Edite só o que for necessário.`);
	};

	const pedirConfirmarModelo = (m) => {
		const temConteudo = String(campanha.subject || "").trim().length > 0 || String(campanha.message || "").trim().length > 0;
		if (!temConteudo) {
			aplicarModelo(m);
			return;
		}
		setModeloPendente(m);
	};

	const confirmarAplicarModelo = (aceitar) => {
		const m = modeloPendente;
		setModeloPendente(null);
		if (!aceitar || !m) return;
		aplicarModelo(m);
	};

	const toggleSegmento = (segId) => {
		setCampanha((prev) => {
			const jaTem = prev.segments.includes(segId);
			return {
				...prev,
				segments: jaTem
					? prev.segments.filter((s) => s !== segId)
					: [...prev.segments, segId],
			};
		});
	};

	const handlePreviewCampanha = async () => {
		if (!String(campanha.subject || "").trim() || !String(campanha.message || "").trim()) {
			toast.error("Informe o assunto e a mensagem para pré-visualizar.");
			return;
		}
		if (!validarEmailsExtras()) {
			toast.error("Corrija os e-mails específicos inválidos antes de pré-visualizar.");
			return;
		}
		const { lista: extraList } = parsearEmailsExtras(campanha.emailsExtras);
		setGerandoPreview(true);
		try {
			const { data } = await api.post(
				"/settings/white-label/preview-info-campaign",
				{
					subject: campanha.subject,
					message: campanha.message,
					segments: campanha.segments,
					emailsExtras: extraList,
				},
				{ timeout: 60_000 }
			);
			if (data.ok) {
				setPreview(data);
			} else {
				toast.error(data.erro || "Não foi possível gerar o preview.");
			}
		} catch (err) {
			toastError(err);
		} finally {
			setGerandoPreview(false);
		}
	};

	const handleEnviarCampanha = async () => {
		if (campanha.segments.length === 0 && !String(campanha.emailsExtras || "").trim()) {
			toast.error("Marque pelo menos um segmento ou informe e-mails específicos.");
			return;
		}
		if (!validarEmailsExtras()) {
			toast.error("Corrija os e-mails específicos inválidos antes de enviar.");
			return;
		}
		if (!String(campanha.subject || "").trim()) {
			toast.error("Informe o assunto da comunicação.");
			return;
		}
		if (!String(campanha.message || "").trim()) {
			toast.error("Escreva a mensagem antes de enviar.");
			return;
		}
		if (!campanha.confirmado) {
			toast.error("Marque a confirmação de que você tem certeza antes de disparar.");
			return;
		}
		const { lista: extraList } = parsearEmailsExtras(campanha.emailsExtras);
		setEnviandoCampanha(true);
		try {
			const { data } = await api.post(
				"/settings/white-label/send-info-campaign",
				{
					segments: campanha.segments,
					emailsExtras: extraList,
					subject: campanha.subject,
					message: campanha.message,
					confirmado: true,
				},
				{ timeout: 60_000 }
			);
			if (data.ok) {
				toast.success(
					<div>
						<div className="font-semibold">Comunicação disparada!</div>
						<div className="text-xs opacity-90 mt-0.5">
							{data.info || "O envio está sendo feito em background."}
						</div>
						<div className="text-[10px] opacity-80 mt-1">
							Destinatários únicos: {data.totalRecipients}
							{data.emailsExtrasCount ? ` · Extras: ${data.emailsExtrasCount}` : ""}
							{data.campanhaId ? ` · Campanha #${data.campanhaId}` : ""}
						</div>
					</div>
				);
				setModalComunicacaoAberto(false);
				setCampanha({ segments: [], emailsExtras: "", subject: "", message: "", confirmado: false });
				setCampanhaErros({ emailsExtras: null });
				setPreview(null);
				setModeloPendente(null);
			} else {
				toast.error(data.erro || "Não foi possível disparar a campanha.");
			}
		} catch (err) {
			toastError(err);
		} finally {
			setEnviandoCampanha(false);
		}
	};

	const handleUploadWhiteLabelFile = async (e, type) => {
		const file = e.target.files?.[0];
		if (!file) return;
		const formData = new FormData();
		formData.append("file", file);
		try {
			const { data } = await api.post(
				`/settings/white-label/${type}`,
				formData
			);
			if (type === "logo") {
				setWhiteLabel((prev) => ({ ...prev, logoUrl: data.logoUrl }));
			} else if (type === "background") {
				setWhiteLabel((prev) => ({
					...prev,
					backgroundUrl: data.backgroundUrl,
				}));
			} else if (type === "hero") {
				setWhiteLabel((prev) => ({
					...prev,
					loginHeroImageUrl: data.loginHeroImageUrl,
				}));
			} else if (type === "login-logo") {
				setWhiteLabel((prev) => ({
					...prev,
					loginLogoUrl: data.loginLogoUrl,
				}));
			} else if (type === "favicon") {
				setWhiteLabel((prev) => ({
					...prev,
					faviconUrl: data.faviconUrl,
				}));
				// Favicon precisa atualizar em tempo real assim que o upload
				// chega (e não só depois do usuário clicar em "Salvar").
				try {
					await Promise.resolve(recarregarWhiteLabel && recarregarWhiteLabel());
				} catch (_) {
					/* idem: falha de aparência não trava o save */
				}
			}
			const mensagens = {
				logo: "Logo enviado com sucesso.",
				background: "Imagem de fundo enviada com sucesso.",
				hero: "Imagem hero do login enviada com sucesso.",
				"login-logo": "Logo exclusivo do login enviado com sucesso.",
				favicon: "Favicon enviado com sucesso.",
			};
			toast.success(mensagens[type] || "Arquivo enviado com sucesso.");
		} catch (err) {
			toastError(err);
		}
		e.target.value = "";
	};

	// ----- Birthdays -----

	const handleSaveBirthdaySettings = async () => {
		setSavingBirthday(true);
		try {
			await api.put("/birthdays/settings", {
				isEnabled: birthdaySettings.isEnabled,
				messageTemplate: birthdaySettings.messageTemplate,
				sendHour: +birthdaySettings.sendHour,
				whatsappId: birthdaySettings.whatsappId
					? +birthdaySettings.whatsappId
					: null,
			});
			toast.success("Configurações de aniversário salvas com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setSavingBirthday(false);
	};

	// ----- 2FA -----

	const handleSetup2FA = async () => {
		setLoading2FA(true);
		try {
			const { data } = await api.get("/auth/2fa/setup");
			setTwoFactorSetup(data);
			setTwoFactorToken("");
		} catch (err) {
			toastError(err);
		}
		setLoading2FA(false);
	};

	const handleEnable2FA = async () => {
		if (twoFactorToken.trim().length < 6) return;
		setLoading2FA(true);
		try {
			await api.post("/auth/2fa/enable", { token: twoFactorToken.trim() });
			setTwoFactorEnabled(true);
			setTwoFactorSetup(null);
			setTwoFactorToken("");
			toast.success("Autenticação em duas etapas ativada com sucesso.");
		} catch (err) {
			toastError(err);
		}
		setLoading2FA(false);
	};

	const handleDisable2FA = async () => {
		if (!senhaParaDesligar2FA.trim()) {
			toast.error("Digite sua senha para desligar a verificação em duas etapas.");
			return;
		}

		setLoading2FA(true);
		try {
			// A senha vai no corpo do DELETE. Sem ela o backend recusa: desligar
			// o segundo fator só com a sessão aberta era o que permitia a quem
			// pegasse uma sessão remover a proteção de vez.
			await api.delete("/auth/2fa/disable", {
				data: { password: senhaParaDesligar2FA },
			});
			setSenhaParaDesligar2FA("");
			setTwoFactorEnabled(false);
			setTwoFactorSetup(null);
			toast.success("Autenticação em duas etapas desativada.");
		} catch (err) {
			toastError(err);
		}
		setLoading2FA(false);
	};

	return (
		<div className="w-full px-4 py-6 sm:px-6 lg:px-8">
			{/* Cabeçalho */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold tracking-tight">
					{i18n.t("settings.title")}
				</h1>
				<p className="mt-1 text-sm text-muted-foreground">
					Ajustes que valem para a empresa inteira.
				</p>
			</div>

			<div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
				{/* Navegação lateral */}
				<nav className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0">
					{sectionsVisiveis.map((section) => {
						const Icon = section.icon;
						const active = activeSection === section.id;
						return (
							<button
								key={section.id}
								type="button"
								onClick={() => setActiveSection(section.id)}
								className={
									"flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors lg:w-full " +
									(active
										? "bg-primary/10 text-primary"
										: "text-muted-foreground hover:bg-accent hover:text-accent-foreground")
								}
							>
								<Icon className="h-4 w-4 shrink-0" />
								{section.label}
							</button>
						);
					})}
				</nav>

				{/* Conteúdo */}
				<div className="min-w-0 max-w-3xl space-y-4">
					{/* Uma linha por aba: para que serve o que está logo abaixo. */}
					<p className="text-sm leading-relaxed text-muted-foreground">
						{sectionsVisiveis.find((s) => s.id === activeSection)?.descricao ??
							SECTIONS_TODAS.find((s) => s.id === activeSection)?.descricao}
					</p>

					{/* ---------------- Geral ---------------- */}
					{activeSection === "geral" && (
						<>
						<SettingsCard
							icon={SlidersHorizontal}
							title="Quem pode criar conta"
							description="Vale para a tela de cadastro, aquela que aparece antes de entrar no sistema."
						>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
								<div>
									<Label className="text-sm font-medium">
										{i18n.t("settings.settings.userCreation.name")}
									</Label>
									<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
										Liberado, qualquer pessoa que abrir o endereço do sistema
										consegue se cadastrar sozinha e entrar.
										<br />
										Bloqueado, só quem já está dentro cria gente nova, pela tela
										de Usuários.
									</p>
								</div>
								{settings.length > 0 && (
									<Select
										value={getSettingValue("userCreation")}
										onValueChange={(v) => handleChangeSetting("userCreation", v)}
									>
										<SelectTrigger className="w-full sm:w-48">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="enabled">
												{i18n.t("settings.settings.userCreation.options.enabled")}
											</SelectItem>
											<SelectItem value="disabled">
												{i18n.t("settings.settings.userCreation.options.disabled")}
											</SelectItem>
										</SelectContent>
									</Select>
								)}
							</div>

							{getSettingValue("userCreation") === "enabled" && (
								<div className="mt-4">
									<Aviso>
										Do jeito que está agora, quem tiver o endereço do sistema
										entra sem você aprovar — inclusive alguém de fora da
										empresa. Se o seu time já está todo cadastrado, o mais
										seguro é bloquear.
									</Aviso>
								</div>
							)}
						</SettingsCard>

						{/* Assinatura das mensagens: trava opcional por cargo */}
						<SettingsCard
							icon={MessageSquareText}
							title="Assinatura do atendente nas mensagens"
							description="Bloqueia quem NÃO pode desligar a assinatura. Administradores e supervisores continuam podendo escolher."
						>
							{settings.length > 0 && (
								<>
									<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
										<div>
											<Label className="text-sm font-medium">
												Forçar assinatura no nome do atendente
											</Label>
											<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
												Ligado: atendentes comuns <strong>não conseguem</strong>{" "}
												desligar o botão Assinar no menu da barra de digitar.
												Supervisores e administradores continuam livres para
												ligar e desligar.
												<br />
												Desligado: todo mundo escolhe se quer assinar o nome ou
												não.
											</p>
										</div>
										<div className="flex items-start gap-3 sm:shrink-0">
											<div className="pt-0.5">
												<Switch
													id="force-sign-messages"
													checked={
														getSettingValue("force_sign_messages") === "enabled"
													}
													onCheckedChange={(v) =>
														handleChangeSetting(
															"force_sign_messages",
															v ? "enabled" : "disabled",
														)
													}
												/>
											</div>
											<Label
												htmlFor="force-sign-messages"
												className="cursor-pointer select-none text-xs leading-relaxed text-muted-foreground hover:text-foreground"
											>
												{getSettingValue("force_sign_messages") === "enabled"
													? "Somente atendentes comuns são bloqueados"
													: "Ninguém é bloqueado — todos escolhem"}
											</Label>
										</div>
									</div>

									<div className="mt-4">
										<Aviso>
											<strong>O nome de quem mandou a mensagem CONTINUA
											aparecendo no balão DENTRO do CRM</strong>, para sua
											equipe saber quem atendeu, mesmo que a assinatura seja
											desligada no texto que sai para o cliente. Quem está do
											lado de fora não vê esse nome, só sua equipe.
										</Aviso>
									</div>

									<hr className="my-4 border-border/60" />

									<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
										<div>
											<Label className="text-sm font-medium">
												Mostrar nome do atendente no balão (apenas dentro do CRM)
											</Label>
											<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
												Ligado: no canto inferior direito do balão da equipe
												aparece o nome de quem enviou a mensagem, <strong>apenas
												quando a assinatura NÃO está no texto</strong>. Não
												mostra duas vezes o mesmo nome.
												<br />
												Desligado: o nome do atendente não aparece em lugar
												nenhum no balão (só no histórico de auditoria).
											</p>
										</div>
										<div className="flex items-start gap-3 sm:shrink-0">
											<div className="pt-0.5">
												<Switch
													id="show-attendant-name"
													checked={
														getSettingValue("show_attendant_name_in_bubble") === "enabled" ||
														getSettingValue("show_attendant_name_in_bubble") === ""
													}
													onCheckedChange={(v) =>
														handleChangeSetting(
															"show_attendant_name_in_bubble",
															v ? "enabled" : "disabled",
														)
													}
												/>
											</div>
											<Label
												htmlFor="show-attendant-name"
												className="cursor-pointer select-none text-xs leading-relaxed text-muted-foreground hover:text-foreground"
											>
												{getSettingValue("show_attendant_name_in_bubble") === "disabled"
													? "Nome do atendente oculto nos balões"
													: "Nome do atendente visível quando não tem assinatura"}
											</Label>
										</div>
									</div>
								</>
							)}
						</SettingsCard>
						</>
					)}

					{/* ---------------- Aparência ---------------- */}
					{activeSection === "aparencia" && ehSuperAdmin && (
						<>
						<SettingsCard
							icon={Palette}
							title="Deixar o sistema com a cara da sua empresa"
							description="Troque o nome, a cor e o logo. Quem usa o sistema vê a sua marca, não a do fornecedor."
							footer={
								<Button onClick={handleSaveWhiteLabel} disabled={savingWhiteLabel}>
									{savingWhiteLabel && <Loader2 className="h-4 w-4 animate-spin" />}
									Aplicar para todo mundo
								</Button>
							}
						>
							<div className="space-y-5">
								<div className="space-y-1.5">
									<Label htmlFor="wl-app-name">Nome que aparece nas telas</Label>
									<Input
										id="wl-app-name"
										value={whiteLabel.appName}
										onChange={(e) =>
											setWhiteLabel((prev) => ({ ...prev, appName: e.target.value }))
										}
										placeholder="Perabit"
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Aparece no canto superior esquerdo e na tela de entrar. Ex.:
										coloque “Atendimento Silva” e é isso que sua equipe lê todo
										dia. Em branco, fica o nome padrão.
									</p>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="wl-primary-color">Cor dos botões</Label>
									<p className="text-xs leading-relaxed text-muted-foreground">
										É a cor dos botões principais e dos destaques. Escolha uma
										cor da sua marca — o quadradinho colorido abre o seletor.
									</p>
									<div className="flex items-center gap-3">
										<input
											id="wl-primary-color"
											type="color"
											value={whiteLabel.primaryColor}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													primaryColor: e.target.value,
												}))
											}
											className="h-10 w-14 cursor-pointer rounded-md border bg-transparent p-1"
										/>
										<Input
											value={whiteLabel.primaryColor}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													primaryColor: e.target.value,
												}))
											}
											className="w-36 font-mono text-xs"
										/>
										<span
											className="hidden h-10 flex-1 rounded-md sm:block"
											style={{ backgroundColor: whiteLabel.primaryColor }}
										/>
									</div>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<Label>Logo da empresa</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Aparece no topo do menu. Use uma imagem com fundo
											transparente, se tiver.
										</p>
										<div className="rounded-lg border border-dashed p-3">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() => logoInputRef.current && logoInputRef.current.click()}
											>
												<Upload className="h-4 w-4" />
												Enviar logo
											</Button>
											{/* Lista explícita em vez de "image/*": o backend
											    recusa .svg, que é XML e pode carregar script.
											    Sem isto o seletor ofereceria um arquivo que só
											    seria recusado depois do envio. */}
											<input
												id="wl-logo"
												type="file"
												accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
												ref={logoInputRef}
												onChange={(e) => handleUploadWhiteLabelFile(e, "logo")}
												className="hidden"
											/>
											<p className="mt-2 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
												{whiteLabel.logoUrl ? (
													<>
														<Check className="h-3 w-3 shrink-0 text-emerald-600" />
														<span className="truncate">{whiteLabel.logoUrl}</span>
													</>
												) : (
													"Nenhum logo enviado ainda"
												)}
											</p>
										</div>
									</div>

									<div className="space-y-2">
										<Label>Imagem de fundo da tela de entrar</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											É o que se vê atrás do formulário de login. Sem imagem,
											fica o fundo padrão.
										</p>
										<div className="rounded-lg border border-dashed p-3">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() =>
													backgroundInputRef.current && backgroundInputRef.current.click()
												}
											>
												<Upload className="h-4 w-4" />
												Enviar imagem
											</Button>
											<input
												id="wl-background"
												type="file"
												accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
												ref={backgroundInputRef}
												onChange={(e) => handleUploadWhiteLabelFile(e, "background")}
												className="hidden"
											/>
											<p className="mt-2 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
												{whiteLabel.backgroundUrl ? (
													<>
														<Check className="h-3 w-3 shrink-0 text-emerald-600" />
														<span className="truncate">
															{whiteLabel.backgroundUrl}
														</span>
													</>
												) : (
													"Nenhuma imagem enviada ainda"
												)}
											</p>
										</div>
									</div>
								</div>
							</div>
						</SettingsCard>

						<SettingsCard
							icon={MessageSquareText}
							title="Tela de Login"
							description="Altere a primeira tela que usuários veem ao acessar o sistema: textos, logo, imagem e o ícone da aba do navegador. Tudo em branco usa os valores padrão do sistema."
						>
							<div className="space-y-5">
								<div className="space-y-1.5">
									<Label htmlFor="wl-login-title">Título principal da vitrine</Label>
									<Input
										id="wl-login-title"
										value={whiteLabel.loginTitle}
										onChange={(e) =>
											setWhiteLabel((prev) => ({ ...prev, loginTitle: e.target.value }))
										}
										placeholder="Um só painel."
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Frase curta e de impacto no topo do lado esquerdo.
									</p>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="wl-login-subtitle">Subtítulo em destaque (verde)</Label>
									<Input
										id="wl-login-subtitle"
										value={whiteLabel.loginSubtitle}
										onChange={(e) =>
											setWhiteLabel((prev) => ({ ...prev, loginSubtitle: e.target.value }))
										}
										placeholder="Controle total do atendimento."
									/>
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="wl-login-description">
										Texto descritivo abaixo do título
									</Label>
									<Textarea
										id="wl-login-description"
										rows={3}
										value={whiteLabel.loginDescription}
										onChange={(e) =>
											setWhiteLabel((prev) => ({
												...prev,
												loginDescription: e.target.value,
											}))
										}
										placeholder="WhatsApp, Facebook e Instagram Direct no mesmo lugar..."
									/>
								</div>

								<hr className="border-border/60" />

								<div>
									<p className="text-sm font-semibold mb-3">Blocos de benefícios (3)</p>
									<div className="space-y-4">
										{(whiteLabel.loginFeatures || []).map((feat, idx) => (
											<div
												key={idx}
												className="rounded-lg border bg-muted/20 p-4 space-y-3"
											>
												<p className="text-xs font-semibold text-muted-foreground">
													Bloco {idx + 1}
												</p>
												<Input
													value={feat?.titulo || ""}
													onChange={(e) =>
														setWhiteLabel((prev) => {
															const novas = [...(prev.loginFeatures || [])];
															novas[idx] = {
																...(novas[idx] || {}),
																titulo: e.target.value,
															};
															return { ...prev, loginFeatures: novas };
														})
													}
													placeholder="Título (ex: Todos os canais numa caixa só)"
												/>
												<Textarea
													rows={2}
													value={feat?.detalhe || ""}
													onChange={(e) =>
														setWhiteLabel((prev) => {
															const novas = [...(prev.loginFeatures || [])];
															novas[idx] = {
																...(novas[idx] || {}),
																detalhe: e.target.value,
															};
															return { ...prev, loginFeatures: novas };
														})
													}
													placeholder="Descrição do benefício"
												/>
											</div>
										))}
									</div>
								</div>

								<hr className="border-border/60" />

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
									<div className="space-y-2">
										<Label>Logo exclusivo do login</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Se vazio, usa o logo geral da marca.
										</p>
										<div className="rounded-lg border border-dashed p-3">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() =>
													loginLogoInputRef.current && loginLogoInputRef.current.click()
												}
											>
												<Upload className="h-4 w-4" />
												Enviar logo do login
											</Button>
											<input
												type="file"
												accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
												ref={loginLogoInputRef}
												onChange={(e) => handleUploadWhiteLabelFile(e, "login-logo")}
												className="hidden"
											/>
											<p className="mt-2 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
												{whiteLabel.loginLogoUrl ? (
													<>
														<Check className="h-3 w-3 shrink-0 text-emerald-600" />
														<span className="truncate">{whiteLabel.loginLogoUrl}</span>
													</>
												) : (
													"Usando o logo geral (padrão)"
												)}
											</p>
										</div>
									</div>

									<div className="space-y-2">
										<Label>Imagem hero (lado esquerdo)</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											A ilustração ao lado dos textos.
										</p>
										<div className="rounded-lg border border-dashed p-3">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() =>
													loginHeroInputRef.current && loginHeroInputRef.current.click()
												}
											>
												<Upload className="h-4 w-4" />
												Enviar imagem hero
											</Button>
											<input
												type="file"
												accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
												ref={loginHeroInputRef}
												onChange={(e) => handleUploadWhiteLabelFile(e, "hero")}
												className="hidden"
											/>
											<p className="mt-2 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
												{whiteLabel.loginHeroImageUrl ? (
													<>
														<Check className="h-3 w-3 shrink-0 text-emerald-600" />
														<span className="truncate">
															{whiteLabel.loginHeroImageUrl}
														</span>
													</>
												) : (
													"Nenhuma imagem hero enviada"
												)}
											</p>
										</div>
									</div>

									<div className="space-y-2">
										<Label>Favicon (aba do navegador)</Label>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Icone que aparece na aba do navegador em todo o sistema.
										</p>
										<div className="rounded-lg border border-dashed p-3">
											<Button
												type="button"
												variant="outline"
												size="sm"
												className="w-full"
												onClick={() =>
													faviconInputRef.current && faviconInputRef.current.click()
												}
											>
												<Upload className="h-4 w-4" />
												Enviar favicon
											</Button>
											<input
												type="file"
												accept=".ico,.jpg,.jpeg,.png,.gif,.webp,.bmp,.svg"
												ref={faviconInputRef}
												onChange={(e) => handleUploadWhiteLabelFile(e, "favicon")}
												className="hidden"
											/>
											<p className="mt-2 flex items-center gap-1.5 truncate text-xs text-muted-foreground">
												{whiteLabel.faviconUrl ? (
													<>
														<Check className="h-3 w-3 shrink-0 text-emerald-600" />
														<span className="truncate">{whiteLabel.faviconUrl}</span>
													</>
												) : (
													"Usando favicon padrão do sistema"
												)}
											</p>
										</div>
									</div>
								</div>

								<div className="pt-2">
									<Aviso>
										<strong>Todos os campos são opcionais.</strong> Deixar em branco
										continua usando o visual padrão da tela de login — nada quebra,
										mesmo em instalações antigas que nunca configuraram nada aqui.
										O favicon atualiza automaticamente em todas as páginas após salvar.
									</Aviso>
								</div>
							</div>
						</SettingsCard>
						</>
					)}

					{/* ---------------- SaaS · E-mail do sistema ---------------- */}
					{activeSection === "saas-email" && ehSuperAdmin && (
						<>
						<SettingsCard
							icon={Mail}
							title="Remetente e servidor de e-mail do SaaS"
							description="Essas credenciais são usadas para envios automáticos, como a recuperação de senha pela tela de login. Só o dono do SaaS (empresa 1) vê e altera esta página."
							footer={
								<div className="flex flex-col sm:flex-row justify-end gap-2 w-full sm:w-auto">
									<Button
										type="button"
										variant="outline"
										onClick={handleTestarSmtp}
										disabled={testandoSmtp || savingWhiteLabel}
									>
										{testandoSmtp ? (
											<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
										) : (
											<Mail className="h-4 w-4 mr-1.5" />
										)}
										Testar conexão enviando e-mail para mim
									</Button>
									<Button
										onClick={handleSaveWhiteLabel}
										disabled={savingWhiteLabel || testandoSmtp}
									>
										{savingWhiteLabel && (
											<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
										)}
										Salvar configuração
									</Button>
								</div>
							}
						>
							<div className="space-y-6">
								<Aviso>
									<strong>Primeiro configure e salve aqui,</strong> depois clique em
									<em> Testar conexão</em> para receber um e-mail de confirmação na
									sua própria conta. Sem esta configuração, o botão
									<em> Esqueci a senha?</em> da tela de login não funciona.
								</Aviso>

								<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
									<div className="space-y-1.5 sm:col-span-1">
										<Label htmlFor="smtp-from-name">Nome do remetente</Label>
										<Input
											id="smtp-from-name"
											value={whiteLabel.smtpFromName}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													smtpFromName: e.target.value,
												}))
											}
											placeholder="Perabit Atendimento"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Nome que aparece na caixa de entrada do cliente.
										</p>
									</div>

									<div className="space-y-1.5 sm:col-span-1">
										<Label htmlFor="smtp-from-email">E-mail do remetente</Label>
										<Input
											id="smtp-from-email"
											type="email"
											value={whiteLabel.smtpFromEmail}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													smtpFromEmail: e.target.value.trim(),
												}))
											}
											placeholder="naoresponda@seu-dominio.com.br"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Endereço real que enviará os e-mails.
										</p>
									</div>
								</div>

								<hr className="border-border/60" />

								<div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
									<div className="space-y-1.5 sm:col-span-2">
										<Label htmlFor="smtp-host">
											<Server className="h-3.5 w-3.5 inline mr-1 align-middle -mt-0.5" />
											Servidor SMTP (Host)
										</Label>
										<Input
											id="smtp-host"
											value={whiteLabel.smtpHost}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													smtpHost: e.target.value.trim(),
												}))
											}
											placeholder="smtp.seu-dominio.com.br"
										/>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="smtp-port">Porta</Label>
										<Input
											id="smtp-port"
											type="number"
											min={1}
											max={65535}
											value={whiteLabel.smtpPort || ""}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													smtpPort: e.target.value ? Number(e.target.value) : 587,
												}))
											}
											placeholder="587"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											587 (STARTTLS), 465 (SSL), 25 (sem TLS).
										</p>
									</div>
								</div>

								<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 pt-1">
									<div>
										<Label className="text-sm font-medium">Conexão segura (TLS / SSL)</Label>
										<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
											Ligado = encripta a conexão com o servidor. Desligue apenas
											se o seu provedor pedir explicitamente.
										</p>
									</div>
									<div className="flex items-start gap-3 sm:shrink-0">
										<div className="pt-0.5">
											<Switch
												id="smtp-secure"
												checked={whiteLabel.smtpSecure}
												onCheckedChange={(v) =>
													setWhiteLabel((prev) => ({
														...prev,
														smtpSecure: v,
													}))
												}
											/>
										</div>
										<Label
											htmlFor="smtp-secure"
											className="cursor-pointer select-none text-xs leading-relaxed text-muted-foreground hover:text-foreground"
										>
											{whiteLabel.smtpSecure
												? "Conexão segura ATIVA (SSL/TLS)"
												: "Conexão sem criptografia (apenas portas internas)"}
										</Label>
									</div>
								</div>

								<hr className="border-border/60" />

								<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor="smtp-user">Usuário SMTP</Label>
										<Input
											id="smtp-user"
											value={whiteLabel.smtpUser}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													smtpUser: e.target.value.trim(),
												}))
											}
											placeholder="naoresponda@seu-dominio.com.br"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Muitas vezes igual ao e-mail do remetente.
										</p>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="smtp-pass">Senha SMTP</Label>
										<Input
											id="smtp-pass"
											type="password"
											autoComplete="new-password"
											value={whiteLabel.smtpPass}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													smtpPass: e.target.value,
												}))
											}
											placeholder={
												whiteLabel.smtpPassIsSet
													? "Preencha apenas se quiser alterar a senha salva"
													: "Digite a senha do usuário SMTP"
											}
										/>
										<div className="mt-1 text-xs leading-relaxed text-muted-foreground">
											{whiteLabel.smtpPassIsSet ? (
												<Badge variant="secondary" className="font-normal">
													<Check className="h-3 w-3 mr-1 text-emerald-600" />
													Senha salva no servidor (não exibida aqui)
												</Badge>
											) : (
												<Badge variant="outline" className="font-normal">
													<AlertTriangle className="h-3 w-3 mr-1 text-amber-600" />
													Nenhuma senha salva ainda
												</Badge>
											)}
										</div>
									</div>
								</div>

								<div className="pt-2">
									<Aviso>
										<strong>Sem senha, nenhum e-mail sai.</strong> Se o seu SMTP
										exige autenticação (gmail, outlook, sendgrid, mailgun, zoho,
										mailtrap etc.), não esqueça de preencher os campos Usuário e
										Senha acima. Para Gmail use uma <em>Senha de App</em>, não a
										senha normal da conta.
									</Aviso>
								</div>
							</div>
						</SettingsCard>

						{/* ----- Segundo card: E-mail Informações ----- */}
						<SettingsCard
							icon={Megaphone}
							title="E-mail Informações · Comunicações e avisos"
							description="Use outro servidor SMTP aqui para novidades do sistema, promoções, correções e avisos de manutenção. O remetente e o destinatário são livres (não força o e-mail de não-resposta do transacional)."
							footer={
								<div className="flex flex-col sm:flex-row justify-end gap-2 w-full sm:w-auto">
									<Button
										type="button"
										variant="outline"
										onClick={handleTestarInfoSmtp}
										disabled={testandoInfoSmtp || savingWhiteLabel || enviandoCampanha}
									>
										{testandoInfoSmtp ? (
											<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
										) : (
											<Mail className="h-4 w-4 mr-1.5" />
										)}
										Testar conexão enviando e-mail para mim
									</Button>
									<Button
										type="button"
										variant="secondary"
										onClick={() => {
											setCampanha({ segments: [], emailsExtras: "", subject: "", message: "", confirmado: false });
											setCampanhaErros({ emailsExtras: null });
											setPreview(null);
											setModalComunicacaoAberto(true);
										}}
										disabled={savingWhiteLabel || testandoInfoSmtp || enviandoCampanha}
									>
										<BellRing className="h-4 w-4 mr-1.5" />
										Criar comunicação
									</Button>
									<Button
										onClick={handleSaveWhiteLabel}
										disabled={savingWhiteLabel || testandoInfoSmtp || enviandoCampanha}
									>
										{savingWhiteLabel && (
											<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
										)}
										Salvar configuração
									</Button>
								</div>
							}
						>
							<div className="space-y-6">
								<Aviso>
									<strong>SMTP separado do transacional.</strong> Enquanto o card
									de cima cuida dos e-mails automáticos (ex.: redefinir senha),
									este card serve para comunicações humanas com seus usuários
									e clientes. O From aparece <em>com o nome que você quiser</em>.
								</Aviso>

								<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
									<div className="space-y-1.5 sm:col-span-1">
										<Label htmlFor="info-smtp-from-name">Nome do remetente</Label>
										<Input
											id="info-smtp-from-name"
											value={whiteLabel.infoSmtpFromName}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													infoSmtpFromName: e.target.value,
												}))
											}
											placeholder="Perabit · Comunicações"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Nome que aparece na caixa de entrada do destinatário.
										</p>
									</div>

									<div className="space-y-1.5 sm:col-span-1">
										<Label htmlFor="info-smtp-from-email">E-mail do remetente</Label>
										<Input
											id="info-smtp-from-email"
											type="email"
											value={whiteLabel.infoSmtpFromEmail}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													infoSmtpFromEmail: e.target.value.trim(),
												}))
											}
											placeholder="comunicacoes@seu-dominio.com.br"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Endereço real que enviará as comunicações.
										</p>
									</div>
								</div>

								<hr className="border-border/60" />

								<div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
									<div className="space-y-1.5 sm:col-span-2">
										<Label htmlFor="info-smtp-host">
											<Server className="h-3.5 w-3.5 inline mr-1 align-middle -mt-0.5" />
											Servidor SMTP (Host)
										</Label>
										<Input
											id="info-smtp-host"
											value={whiteLabel.infoSmtpHost}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													infoSmtpHost: e.target.value.trim(),
												}))
											}
											placeholder="smtp.seu-dominio.com.br"
										/>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="info-smtp-port">Porta</Label>
										<Input
											id="info-smtp-port"
											type="number"
											min={1}
											max={65535}
											value={whiteLabel.infoSmtpPort || ""}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													infoSmtpPort: e.target.value ? Number(e.target.value) : 587,
												}))
											}
											placeholder="587"
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											587 (STARTTLS), 465 (SSL), 2525 (alternativa).
										</p>
									</div>
								</div>

								<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 pt-1">
									<div>
										<Label className="text-sm font-medium">Conexão segura (TLS / SSL)</Label>
										<p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
											Ligado = encripta a conexão. Desligue só se o provedor pedir.
										</p>
									</div>
									<div className="flex items-start gap-3 sm:shrink-0">
										<div className="pt-0.5">
											<Switch
												id="info-smtp-secure"
												checked={whiteLabel.infoSmtpSecure}
												onCheckedChange={(v) =>
													setWhiteLabel((prev) => ({
														...prev,
														infoSmtpSecure: v,
													}))
												}
											/>
										</div>
										<Label
											htmlFor="info-smtp-secure"
											className="cursor-pointer select-none text-xs leading-relaxed text-muted-foreground hover:text-foreground"
										>
											{whiteLabel.infoSmtpSecure
												? "Conexão segura ATIVA (SSL/TLS)"
												: "Conexão sem criptografia (apenas portas internas)"}
										</Label>
									</div>
								</div>

								<hr className="border-border/60" />

								<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor="info-smtp-user">Usuário SMTP</Label>
										<Input
											id="info-smtp-user"
											value={whiteLabel.infoSmtpUser}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													infoSmtpUser: e.target.value.trim(),
												}))
											}
											placeholder="comunicacoes@seu-dominio.com.br"
										/>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="info-smtp-pass">Senha SMTP</Label>
										<Input
											id="info-smtp-pass"
											type="password"
											autoComplete="new-password"
											value={whiteLabel.infoSmtpPass}
											onChange={(e) =>
												setWhiteLabel((prev) => ({
													...prev,
													infoSmtpPass: e.target.value,
												}))
											}
											placeholder={
												whiteLabel.infoSmtpPassIsSet
													? "Preencha apenas se quiser alterar a senha salva"
													: "Digite a senha do usuário SMTP informativo"
											}
										/>
										<div className="mt-1 text-xs leading-relaxed text-muted-foreground">
											{whiteLabel.infoSmtpPassIsSet ? (
												<Badge variant="secondary" className="font-normal">
													<Check className="h-3 w-3 mr-1 text-emerald-600" />
													Senha salva no servidor (não exibida aqui)
												</Badge>
											) : (
												<Badge variant="outline" className="font-normal">
													<AlertTriangle className="h-3 w-3 mr-1 text-amber-600" />
													Nenhuma senha salva ainda
												</Badge>
											)}
										</div>
									</div>
								</div>

								<div className="pt-2">
									<Aviso>
										<strong>Não é o mesmo e-mail do card de cima.</strong>
										Se seu provedor bloqueia envio em massa na mesma conta do
										transacional, cadastre aqui uma conta separada. Avisos de
										manutenção, correções e promoções geralmente vem de outro
										endereço para não serem marcados como SPAM.
									</Aviso>
								</div>
							</div>
						</SettingsCard>
						</>
					)}

					{/* Dialog: Nova Comunicação */}
					{ehSuperAdmin && (
						<Dialog
							open={modalComunicacaoAberto}
							onOpenChange={(o) => {
								if (!o) {
									setModalComunicacaoAberto(false);
									setPreview(null);
									setModeloPendente(null);
								} else {
									setModalComunicacaoAberto(true);
								}
							}}
						>
							<DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto">
								<DialogHeader>
									<DialogTitle className="flex items-center gap-2">
										<BellRing className="h-5 w-5 text-primary" />
										Nova Comunicação (E-mail Informações)
									</DialogTitle>
									<DialogDescription>
										Envie um comunicado em massa (informações do sistema, promoções,
										correções ou manutenção). O disparo é feito em background em
										lotes de 10 e-mails, com pausa entre lotes. E-mails duplicados
										são removidos automaticamente.
									</DialogDescription>
								</DialogHeader>

								<div className="space-y-5">
									<div>
										<div className="flex items-center justify-between mb-2">
											<Label className="text-sm font-medium">
												1. Selecione os segmentos de destinatários
											</Label>
											<Badge variant="secondary" className="font-normal">
												<Users className="h-3 w-3 mr-1" />
												{campanha.segments.length > 0
													? `${campanha.segments.length} grupo(s) marcado(s)`
													: "Marque pelo menos 1 grupo"}
											</Badge>
										</div>
										<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
											{SEGMENTOS_CAMPANHA.map((seg) => {
												const marcado = campanha.segments.includes(seg.id);
												const Icone = seg.icone;
												return (
													<label
														key={seg.id}
														className={[
															"flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-all",
															marcado
																? "border-primary/60 bg-primary/5 shadow-sm"
																: "border-border/70 hover:border-primary/40 hover:bg-muted/30",
														].join(" ")}
													>
														<div className="pt-0.5 shrink-0">
															<Checkbox
																checked={marcado}
																onCheckedChange={() => toggleSegmento(seg.id)}
															/>
														</div>
														<div className="min-w-0 flex-1">
															<div className="flex items-center gap-2">
																<Icone className="h-4 w-4 text-primary shrink-0" />
																<div className="text-sm font-medium truncate">{seg.titulo}</div>
															</div>
															<div className="text-xs leading-snug text-muted-foreground mt-1">
																{seg.descricao}
															</div>
														</div>
													</label>
												);
											})}
										</div>
									</div>

									<hr className="border-border/60" />

									<div className="space-y-3">
										<div className="flex items-center justify-between gap-3">
											<Label className="text-sm font-medium">
												1b. E-mails específicos (opcional)
											</Label>
											<Badge variant="outline" className="font-normal">
												{(() => {
													const { lista, invalidos } = parsearEmailsExtras(campanha.emailsExtras);
													return invalidos.length
														? `${lista.length} válido(s) · ${invalidos.length} inválido(s)`
														: lista.length
															? `${lista.length} e-mail(s) único(s)`
															: "Separe por vírgula ou enter";
												})()}
											</Badge>
										</div>
										<div className="space-y-1.5">
											<Textarea
												value={campanha.emailsExtras}
												rows={3}
												onChange={(e) => {
													const valor = e.target.value;
													setCampanha((prev) => ({ ...prev, emailsExtras: valor }));
													const { invalidos } = parsearEmailsExtras(valor);
													setCampanhaErros((prev) => ({
														...prev,
														emailsExtras: invalidos.length
															? `${invalidos.length} e-mail(s) inválido(s): ${invalidos.slice(0, 3).join(", ")}${invalidos.length > 3 ? "…" : ""}`
															: null,
													}));
												}}
												placeholder="fulano@empresa.com, maria@cliente.com.br
sistema@exemplo.org"
											/>
											{campanhaErros.emailsExtras ? (
												<p className="text-xs text-red-600 dark:text-red-400 font-medium">
													{campanhaErros.emailsExtras}
												</p>
											) : (
												<p className="text-xs text-muted-foreground">
													Informe endereços avulsos (fora dos grupos marcados).
													E-mails repetidos ou inválidos são automaticamente
													ignorados no disparo.
												</p>
											)}
										</div>
									</div>

									<hr className="border-border/60" />

									<div className="space-y-3">
										<div className="flex items-center justify-between gap-3">
											<Label className="text-sm font-medium">
												1c. Modelos prontos (opcional)
											</Label>
											{modeloPendente && (
												<Badge variant="outline" className="text-[11px] font-normal">
													Pronto para selecionar · só edite o que precisar
												</Badge>
											)}
										</div>
										<p className="text-xs text-muted-foreground">
											Escolha um modelo para começar mais rápido. Ele já preenche
											o assunto e o conteúdo com uma formatação que funciona no
											preview, usando os mesmos placeholders dos badges abaixo.
										</p>
										<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
											{MODELOS_COMUNICACAO.map((m) => {
												const Icone = m.icone;
												return (
													<button
														type="button"
														key={m.id}
														onClick={() => pedirConfirmarModelo(m)}
														className={[
															"group text-left rounded-xl border transition-all p-2.5",
															"flex items-start gap-2.5 bg-transparent",
															"focus:outline-none focus:ring-2 focus:ring-primary/30",
															"overflow-hidden",
															String(m.cor || "border-border hover:bg-muted/30"),
														].join(" ")}
													>
														<div className="shrink-0 mt-0.5">
															<div className="h-8 w-8 rounded-lg flex items-center justify-center border border-border/70 bg-muted/40 group-hover:bg-white group-hover:border-primary/50 transition-colors">
																<Icone className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
															</div>
														</div>
														<div className="min-w-0 flex-1">
															<div className="text-sm font-semibold leading-snug text-foreground break-words">
																{m.titulo}
															</div>
															<div className="text-[11px] text-muted-foreground leading-snug mt-0.5 line-clamp-2 break-words">
																{m.descricao}
															</div>
															<div className="mt-1.5 flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
																<span className="inline-flex items-center gap-1 rounded-md bg-muted/50 border border-border/60 px-1.5 py-0.5 whitespace-nowrap">
																	Assunto
																</span>
																<span className="inline-flex items-center gap-1 rounded-md bg-muted/50 border border-border/60 px-1.5 py-0.5 whitespace-nowrap">
																	HTML
																</span>
																<span className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary border border-primary/25 px-1.5 py-0.5 whitespace-nowrap">
																	Placeholders
																</span>
															</div>
														</div>
													</button>
												);
											})}
										</div>
									</div>

									{modeloPendente && (
										<div className="rounded-xl border border-primary/35 bg-primary/5 p-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
											<div className="min-w-0">
												<div className="text-sm font-semibold text-foreground flex items-center gap-1.5">
													<AlertTriangle className="h-4 w-4 text-primary shrink-0" />
													Já existe conteúdo escrito
												</div>
												<div className="text-xs text-muted-foreground mt-0.5 leading-snug">
													Você quer aplicar o modelo <strong>{modeloPendente.titulo}</strong> e <strong>substituir</strong> o assunto e a mensagem atuais?
													O que já foi escrito será substituído pelo modelo.
												</div>
											</div>
											<div className="flex gap-2 shrink-0">
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => confirmarAplicarModelo(false)}
												>
													Cancelar
												</Button>
												<Button
													type="button"
													size="sm"
													onClick={() => confirmarAplicarModelo(true)}
												>
													Sim, usar este modelo
												</Button>
											</div>
										</div>
									)}

									<hr className="border-border/60" />

									<div className="space-y-4">
										<Label className="text-sm font-medium">
											2. Escreva a comunicação
										</Label>
										<div className="space-y-1.5">
											<Label htmlFor="campanha-subject">Assunto (máx. 250 caracteres)</Label>
											<Input
												id="campanha-subject"
												value={campanha.subject}
												maxLength={250}
												onChange={(e) =>
													setCampanha((prev) => ({
														...prev,
														subject: e.target.value.slice(0, 250),
													}))
												}
												placeholder="Ex.: Aviso programado de manutenção no sábado"
											/>
											<div className="flex justify-end">
												<span className="text-[11px] text-muted-foreground">
													{campanha.subject.length}/250
												</span>
											</div>
										</div>

										<div className="space-y-2">
											<Label htmlFor="campanha-message">
												Mensagem · use a barra abaixo para formatar
											</Label>
											<div className="flex flex-wrap items-center gap-1 p-1.5 rounded-lg border border-border/70 bg-muted/20">
												{[
													{
														tag: "strong",
														label: "B",
														title: "Negrito (Ctrl+B)",
														before: "<strong>",
														after: "</strong>",
														ph: "texto em destaque",
														weight: "font-bold",
													},
													{
														tag: "em",
														label: "I",
														title: "Itálico",
														before: "<em>",
														after: "</em>",
														ph: "texto em itálico",
														weight: "italic",
													},
													{
														tag: "u",
														label: "S",
														title: "Sublinhado",
														before: "<u>",
														after: "</u>",
														ph: "texto sublinhado",
														weight: "underline",
													},
													{ tag: "sep" },
													{
														tag: "h2",
														label: "Título",
														title: "Título",
														before: "\n<h2>",
														after: "</h2>\n",
														ph: "Título da seção",
													},
													{
														tag: "p",
														label: "¶",
														title: "Parágrafo",
														before: "\n<p>",
														after: "</p>\n",
														ph: "seu parágrafo aqui",
													},
													{
														tag: "a",
														label: "Link",
														title: "Link (URL)",
														before: '<a href="',
														after: '">clique aqui</a>',
														ph: "https://",
													},
													{
														tag: "ul",
														label: "• Lista",
														title: "Lista não ordenada",
														before: "\n<ul>\n  <li>",
														after: "</li>\n  <li>item 2</li>\n</ul>\n",
														ph: "item 1",
													},
													{
														tag: "quote",
														label: `" Citação"`,
														title: "Citação em destaque",
														before: "\n<blockquote>",
														after: "</blockquote>\n",
														ph: "texto em destaque / aviso",
													},
													{
														tag: "hr",
														label: "— Linha",
														title: "Linha separadora",
														before: "\n<hr />\n",
														after: "",
														ph: "",
													},
													{ tag: "sep" },
													{
														tag: "ph1",
														label: "{{primeiroNome}}",
														title: "Inserir primeiro nome",
														onClick: () => inserirTextoMensagem("{{primeiroNome}}", "", "Maria"),
													},
													{
														tag: "ph2",
														label: "{{nome}}",
														title: "Inserir nome completo",
														onClick: () => inserirTextoMensagem("{{nome}}", "", "Maria Silva"),
													},
													{
														tag: "ph3",
														label: "{{email}}",
														title: "Inserir e-mail",
														onClick: () => inserirTextoMensagem("{{email}}", "", "maria@empresa.com"),
													},
													{
														tag: "ph4",
														label: "{{empresa}}",
														title: "Inserir nome da empresa",
														onClick: () => inserirTextoMensagem("{{empresa}}", "", "Empresa LTDA"),
													},
												].map((btn, idx) =>
													btn.tag === "sep" ? (
														<span
															key={`sep-${idx}`}
															className="w-px h-5 bg-border/70 mx-1"
														/>
													) : (
														<Button
															key={btn.tag + idx}
															type="button"
															size="sm"
															variant="ghost"
															title={btn.title || btn.label}
															onClick={() => {
																if (btn.onClick) return btn.onClick();
																inserirTextoMensagem(btn.before, btn.after, btn.ph || "");
															}}
															className={[
																"h-8 px-2.5 text-xs",
																btn.weight || "",
																typeof btn.tag === "string" && btn.tag.startsWith("ph")
																	? "font-mono text-[10px] text-primary hover:text-primary"
																	: "",
															].join(" ")}
														>
															{btn.label}
														</Button>
													)
												)}
											</div>

											<Textarea
												id="campanha-message"
												ref={(el) => setMsgRef(el)}
												value={campanha.message}
												rows={12}
												spellCheck={false}
												style={{ fontFamily:
													"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
													fontSize: 13, lineHeight: 1.65 }}
												onChange={(e) =>
													setCampanha((prev) => ({
														...prev,
														message: e.target.value,
													}))
												}
												placeholder={`<h2>Olá {{primeiroNome}}!</h2>
<p>Estamos entrando em contato para avisar sobre <strong>novidades</strong> no sistema.</p>
<ul>
  <li>Item um</li>
  <li>Item dois</li>
</ul>
<p>Qualquer dúvida é só responder este e-mail.</p>`}
											/>
											<div className="flex items-center justify-between gap-3 pt-1">
												<div className="flex flex-wrap gap-1.5">
													{[
														{ ph: "{{primeiroNome}}", icone: UserRound },
														{ ph: "{{nome}}", icone: Users },
														{ ph: "{{email}}", icone: Mail },
														{ ph: "{{empresa}}", icone: Building2 },
													].map(({ ph, icone: Icone }) => (
														<Badge
															key={ph}
															variant="outline"
															className="font-mono text-[11px] cursor-pointer select-none hover:bg-primary/10 hover:border-primary/40 transition-colors"
															title={`Clique para inserir "${ph}" no cursor`}
															onClick={() => inserirTextoMensagem(ph, "", ph.replace(/[{}]/g, ""))}
														>
															<Icone className="h-2.5 w-2.5 mr-1 opacity-70" />
															{ph}
														</Badge>
													))}
												</div>
												<p className="text-[11px] text-muted-foreground shrink-0">
													{campanha.message.length} caracteres
												</p>
											</div>
										</div>
									</div>

									<hr className="border-border/60" />

									<label className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5 cursor-pointer">
										<div className="pt-0.5 shrink-0">
											<Checkbox
												checked={campanha.confirmado}
												onCheckedChange={(v) =>
													setCampanha((prev) => ({
														...prev,
														confirmado: Boolean(v),
													}))
												}
											/>
										</div>
										<div className="min-w-0">
											<div className="text-sm font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
												<AlertTriangle className="h-4 w-4 shrink-0" />
												Tenho certeza de que quero disparar esta comunicação
											</div>
											<div className="text-xs leading-snug text-amber-700/90 dark:text-amber-200/90 mt-1">
												O envio é em massa e dispara automaticamente em background.
												Marque só depois de ter salvo os dados SMTP (cima),
												testado a conexão e conferido o Preview. Não é possível
												cancelar depois do início do disparo.
											</div>
										</div>
									</label>
								</div>

								<DialogFooter className="flex-col sm:flex-row gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={() => {
											setModalComunicacaoAberto(false);
											setPreview(null);
											setModeloPendente(null);
										}}
										disabled={enviandoCampanha || gerandoPreview}
									>
										Cancelar
									</Button>
									<Button
										type="button"
										variant="secondary"
										onClick={handlePreviewCampanha}
										disabled={
											gerandoPreview ||
											enviandoCampanha ||
											!String(campanha.subject || "").trim() ||
											!String(campanha.message || "").trim() ||
											Boolean(campanhaErros.emailsExtras)
										}
									>
										{gerandoPreview ? (
											<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
										) : (
											<Eye className="h-4 w-4 mr-1.5" />
										)}
										Pré-visualizar (exemplo com você mesmo)
									</Button>
									<Button
										type="button"
										onClick={handleEnviarCampanha}
										disabled={
											enviandoCampanha ||
											gerandoPreview ||
											(campanha.segments.length === 0 && !String(campanha.emailsExtras || "").trim()) ||
											!String(campanha.subject || "").trim() ||
											!String(campanha.message || "").trim() ||
											!campanha.confirmado ||
											Boolean(campanhaErros.emailsExtras)
										}
									>
										{enviandoCampanha ? (
											<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
										) : (
											<SendHorizonal className="h-4 w-4 mr-1.5" />
										)}
										Confirmar disparo
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					)}

					{/* Dialog: Preview da Comunicação */}
					{ehSuperAdmin && (
						<Dialog
							open={!!preview}
							onOpenChange={(o) => {
								if (!o) setPreview(null);
							}}
						>
							<DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-hidden">
								<DialogHeader>
									<DialogTitle className="flex items-center gap-2">
										<Eye className="h-5 w-5 text-primary" />
										Preview da comunicação
									</DialogTitle>
									<DialogDescription>
										Exemplo de como a mensagem chegaria para o destinatário
										{preview?.previewTo ? ` (${preview.previewTo})` : ""}.
										{preview?.subject ? (
											<>
												{" "}Assunto:
												<strong>{preview.subject}</strong>
											</>
										) : null}
									</DialogDescription>
								</DialogHeader>
								<div className="rounded-xl border border-border overflow-hidden bg-muted/20">
									{preview?.html ? (
										<iframe
											title="Preview Comunicação"
											srcDoc={preview.html}
											sandbox="allow-same-origin"
											className="w-full h-[55vh] bg-white"
										/>
									) : preview?.textPreview ? (
										<div className="p-4 text-sm whitespace-pre-line">
											{preview.textPreview}
										</div>
									) : null}
								</div>
								<DialogFooter>
									<Button
										type="button"
										variant="outline"
										onClick={() => setPreview(null)}
									>
										Voltar para edição
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					)}

					{/* ---------------- Automações ---------------- */}
					{activeSection === "automacoes" && (
						<SettingsCard
							icon={Cake}
							title="Parabéns de aniversário"
							description="Ligado, o contato recebe uma mensagem de parabéns no dia do aniversário dele, sem ninguém precisar lembrar."
							action={
								<div className="flex items-center gap-2">
									<Label
										htmlFor="birthday-enabled"
										className="text-xs font-normal text-muted-foreground"
									>
										{birthdaySettings.isEnabled ? "Ligado" : "Desligado"}
									</Label>
									<Switch
										id="birthday-enabled"
										checked={birthdaySettings.isEnabled}
										onCheckedChange={(checked) =>
											setBirthdaySettings((prev) => ({
												...prev,
												isEnabled: checked,
											}))
										}
									/>
								</div>
							}
							footer={
								<Button
									onClick={handleSaveBirthdaySettings}
									disabled={savingBirthday}
								>
									{savingBirthday && <Loader2 className="h-4 w-4 animate-spin" />}
									Salvar os parabéns automáticos
								</Button>
							}
						>
							<div className="space-y-5">
								{/* Armadilha real: as configurações são gravadas, mas hoje
								    nada no servidor dispara o envio no horário marcado. */}
								<Aviso>
									<strong>O envio automático ainda não está funcionando.</strong>{" "}
									O que você configurar aqui fica guardado, mas nenhuma mensagem
									sai sozinha por enquanto. Por ora, os aniversariantes do dia
									aparecem na lista mais abaixo para você mandar o parabéns na
									mão.
								</Aviso>

								<div className="space-y-1.5">
									<Label htmlFor="birthday-template">
										O que o contato vai receber
									</Label>
									<Textarea
										id="birthday-template"
										rows={3}
										value={birthdaySettings.messageTemplate}
										onChange={(e) =>
											setBirthdaySettings((prev) => ({
												...prev,
												messageTemplate: e.target.value,
											}))
										}
										placeholder="Feliz aniversário, {{name}}! 🎉"
									/>
									<p className="text-xs leading-relaxed text-muted-foreground">
										Escreva {"{{name}}"} onde o nome da pessoa deve entrar. Ex.:
										“Feliz aniversário, {"{{name}}"}!” chega no celular da Maria
										como “Feliz aniversário, Maria!”. Em branco, o sistema usa
										um parabéns simples.
									</p>
								</div>

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor="birthday-hour">
											A que horas mandar
										</Label>
										<Input
											id="birthday-hour"
											type="number"
											min={0}
											max={23}
											value={birthdaySettings.sendHour}
											onChange={(e) =>
												setBirthdaySettings((prev) => ({
													...prev,
													sendHour: e.target.value,
												}))
											}
										/>
										<p className="text-xs leading-relaxed text-muted-foreground">
											Use a hora cheia, de 0 a 23. Ex.: 9 é nove da manhã, 14 é
											duas da tarde. Evite madrugada — a mensagem chega e
											acorda o cliente.
										</p>
									</div>
									<div className="space-y-1.5">
										<Label>Qual número vai mandar</Label>
										<Select
											value={
												birthdaySettings.whatsappId
													? String(birthdaySettings.whatsappId)
													: undefined
											}
											onValueChange={(v) =>
												setBirthdaySettings((prev) => ({
													...prev,
													whatsappId: +v,
												}))
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Escolha o número" />
											</SelectTrigger>
											<SelectContent>
												{whatsApps.map((w) => (
													<SelectItem key={w.id} value={String(w.id)}>
														{w.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<p className="text-xs leading-relaxed text-muted-foreground">
											É o WhatsApp que aparece para o cliente como remetente.
											Sem escolher um número, nada é enviado.
										</p>
									</div>
								</div>

								{/* A lista fica recolhida, mas o resumo já responde
								    "tem alguém fazendo aniversário hoje?" sem abrir. */}
								<div className="rounded-lg border bg-muted/30">
									<button
										type="button"
										onClick={() => setListaAniversarioAberta((v) => !v)}
										className="flex w-full items-center gap-2 p-4 text-left"
									>
										{listaAniversarioAberta ? (
											<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
										) : (
											<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
										)}
										<span className="min-w-0 flex-1">
											<span className="block text-sm font-medium">
												Quem faz aniversário hoje
											</span>
											<span className="mt-0.5 block text-xs text-muted-foreground">
												{todayBirthdays.length === 0
													? "Ninguém hoje."
													: todayBirthdays.length === 1
														? "1 pessoa faz aniversário hoje."
														: `${todayBirthdays.length} pessoas fazem aniversário hoje.`}
											</span>
										</span>
										<Badge variant="secondary">{todayBirthdays.length}</Badge>
									</button>
									{listaAniversarioAberta && (
										<div className="border-t px-4 py-3">
											{todayBirthdays.length === 0 ? (
												<p className="text-xs leading-relaxed text-muted-foreground">
													Ninguém da sua lista de contatos faz aniversário
													hoje. Quem não tem a data de nascimento preenchida no
													cadastro nunca aparece aqui.
												</p>
											) : (
												<ul className="space-y-1 text-sm">
													{todayBirthdays.map((contact) => (
														<li
															key={contact.id}
															className="text-muted-foreground"
														>
															{contact.name}
															{contact.number ? ` — ${contact.number}` : ""}
														</li>
													))}
												</ul>
											)}
										</div>
									)}
								</div>
							</div>
						</SettingsCard>

					)}

					{/* ---------------- Segurança ---------------- */}
					{activeSection === "seguranca" && (
						<SettingsCard
							icon={ShieldCheck}
							title="Pedir um código extra ao entrar"
							description="Ligado, além da senha o sistema pede um número de 6 dígitos que só aparece no seu celular. Se roubarem sua senha, ainda não conseguem entrar."
							action={
								twoFactorEnabled ? (
									<Badge className="gap-1">
										<Check className="h-3 w-3" />
										Ativada
									</Badge>
								) : (
									<Badge variant="secondary">Desativada</Badge>
								)
							}
						>
							{twoFactorEnabled ? (
								<div className="space-y-4">
									<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
										<p className="text-sm leading-relaxed text-muted-foreground">
											Está ligado. Toda vez que você entrar, além da senha o
											sistema vai pedir o código do seu aplicativo.
										</p>
										<div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
											{/* A senha fica ao lado do botão, não num modal: é um
											    campo só, e um modal para uma linha esconderia o
											    motivo de ela estar sendo pedida. */}
											<Input
												type="password"
												className="sm:w-56"
												placeholder="Sua senha"
												autoComplete="current-password"
												value={senhaParaDesligar2FA}
												onChange={(e) => setSenhaParaDesligar2FA(e.target.value)}
											/>
											<Button
												variant="outline"
												className="shrink-0 text-destructive hover:text-destructive"
												onClick={handleDisable2FA}
												disabled={loading2FA || !senhaParaDesligar2FA.trim()}
											>
												{loading2FA && <Loader2 className="h-4 w-4 animate-spin" />}
												Voltar a entrar só com a senha
											</Button>
										</div>
									</div>
									<Aviso>
										Se você trocar de celular ou apagar o aplicativo sem
										desligar isto antes, você fica sem o código e não consegue
										mais entrar. Desligue aqui primeiro.
									</Aviso>
								</div>
							) : twoFactorSetup ? (
								<div className="space-y-4">
									<p className="text-sm leading-relaxed text-muted-foreground">
										No celular, instale um aplicativo de códigos (Google
										Authenticator ou Authy, ambos gratuitos), abra a câmera dele
										e aponte para o quadrado abaixo. O aplicativo passa a
										mostrar um número de 6 dígitos que troca a cada 30 segundos.
										Digite o número atual para confirmar.
									</p>
									<div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
										{twoFactorSetup.qrCodeDataUrl && (
											<img
												src={twoFactorSetup.qrCodeDataUrl}
												alt="QR Code 2FA"
												className="h-44 w-44 shrink-0 rounded-lg border bg-white p-2"
											/>
										)}
										<div className="w-full flex-1 space-y-3">
											<div className="space-y-1.5">
												<Label className="text-xs">
													Se a câmera não ler, digite este código no aplicativo
												</Label>
												<Input
													readOnly
													value={twoFactorSetup.secret || ""}
													className="font-mono text-xs"
												/>
											</div>
											<div className="space-y-1.5">
												<Label htmlFor="twofa-token">
													Número que apareceu no aplicativo
												</Label>
												<Input
													id="twofa-token"
													inputMode="numeric"
													maxLength={6}
													placeholder="000000"
													value={twoFactorToken}
													onChange={(e) =>
														setTwoFactorToken(e.target.value.replace(/\D/g, ""))
													}
													className="font-mono tracking-widest"
												/>
											</div>
											<div className="flex gap-2">
												<Button
													variant="outline"
													onClick={() => setTwoFactorSetup(null)}
													disabled={loading2FA}
												>
													Cancelar
												</Button>
												<Button
													onClick={handleEnable2FA}
													disabled={loading2FA || twoFactorToken.length < 6}
												>
													{loading2FA && (
														<Loader2 className="h-4 w-4 animate-spin" />
													)}
													Conferir e ligar a proteção
												</Button>
											</div>
										</div>
									</div>
								</div>
							) : (
								<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
									<p className="text-sm leading-relaxed text-muted-foreground">
										Hoje basta a sua senha para entrar. Você vai precisar de um
										celular com um aplicativo de códigos — leva uns dois
										minutos para configurar. Vale só para a sua conta, não
										muda nada para os outros usuários.
									</p>
									<Button
										className="shrink-0"
										onClick={handleSetup2FA}
										disabled={loading2FA}
									>
										{loading2FA && <Loader2 className="h-4 w-4 animate-spin" />}
										Começar a configurar
									</Button>
								</div>
							)}
						</SettingsCard>
					)}

					{/* ---------------- API ---------------- */}
					{activeSection === "api" && (
						<>
							<SettingsCard
								icon={KeyRound}
								title="Chave para ligar outro sistema a este"
								description="Serve para um programa de fora (seu site, uma loja, uma planilha) mandar mensagem por aqui sozinho. Se você não vai integrar nada, é só ignorar esta aba."
							>
								<div className="space-y-1.5">
									<Label htmlFor="api-token-setting">
										Sua chave — copie e entregue a quem for fazer a integração
									</Label>
									<div className="flex gap-2">
										<Input
											id="api-token-setting"
											readOnly
											value={settings.length > 0 ? getSettingValue("userApiToken") : ""}
											className="font-mono text-xs"
										/>
										<Button
											type="button"
											variant="outline"
											size="icon"
											onClick={handleCopyToken}
											title="Copiar a chave"
										>
											<Copy className="h-4 w-4" />
										</Button>
									</div>
									<div className="pt-2">
										<Aviso>
											Trate esta chave como a senha do cofre: quem tiver ela
											consegue mandar mensagem de WhatsApp em nome da sua empresa,
											para qualquer cliente, sem precisar entrar no sistema. Não
											mande por grupo, e-mail aberto nem cole em conversa.
										</Aviso>
									</div>

									{/* A documentação mora numa tela só, com exemplos prontos e
									    um testador. Repetir o conteúdo aqui garantiria que uma
									    das duas cópias ficasse desatualizada. */}
									<div className="border-t pt-4">
										<p className="text-sm font-medium">
											Como usar em outro sistema
										</p>
										<p className="mt-1 text-sm leading-relaxed text-muted-foreground">
											Endereço, campos, exemplos prontos de código e um testador
											que envia de verdade estão na tela da API.
										</p>
										<Button
											type="button"
											variant="outline"
											className="mt-3"
											onClick={() => navigate("/messages-api")}
										>
											<Plug className="h-4 w-4" />
											Abrir documentação da API
										</Button>
									</div>
								</div>
							</SettingsCard>

							<SettingsCard
								icon={CalendarDays}
								title="Google Calendar do fluxo"
								description="Conecte a conta do Google que vai cuidar da agenda usada pelos seus fluxos de conversa."
								action={
									<Button
										type="button"
										variant="ghost"
										size="icon"
										aria-label="Como conectar o Google Calendar"
										title="Como conectar o Google Calendar"
										onClick={() => setTutorialGoogleCalendar((v) => !v)}
									>
										<Info className="h-4 w-4" />
									</Button>
								}
								footer={
									<div className="flex justify-end">
										<Button
											type="button"
											onClick={handleSaveGoogleCalendar}
											disabled={savingGoogleCalendar}
										>
											{savingGoogleCalendar && (
												<Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
											)}
											Salvar Google Calendar
										</Button>
									</div>
								}
							>
								<div className="space-y-5">
									<Aviso>
										Preencha com as credenciais do seu app OAuth do Google
										Cloud. É com elas que o sistema cria eventos e consulta a
										agenda nos fluxos de conversa.
									</Aviso>

									<Dialog
										open={tutorialGoogleCalendar}
										onOpenChange={setTutorialGoogleCalendar}
									>
										<DialogContent className="sm:max-w-xl">
											<DialogHeader>
												<DialogTitle>Como conectar o Google Calendar</DialogTitle>
												<DialogDescription>
													O sistema precisa de três credenciais: client id,
													client secret e refresh token.
												</DialogDescription>
											</DialogHeader>
											<ol className="list-decimal space-y-2 pl-4 text-xs leading-relaxed text-muted-foreground">
												<li>
													Entre no{" "}
													<a
														href="https://console.cloud.google.com/"
														target="_blank"
														rel="noreferrer"
														className="underline underline-offset-2 hover:text-foreground"
													>
														Google Cloud Console
													</a>{" "}
													com a conta Google que vai gerenciar a agenda e crie um
													projeto (ou use um que já exista).
												</li>
												<li>
													Em{" "}
													<a
														href="https://console.cloud.google.com/apis/library/calendar-json.googleapis.com"
														target="_blank"
														rel="noreferrer"
														className="underline underline-offset-2 hover:text-foreground"
													>
														APIs e serviços › Biblioteca
													</a>
													, procure por{" "}
													<strong className="font-medium text-foreground">
														Google Calendar API
													</strong>{" "}
													e clique em Ativar.
												</li>
												<li>
													Em{" "}
													<a
														href="https://console.cloud.google.com/apis/credentials/consent"
														target="_blank"
														rel="noreferrer"
														className="underline underline-offset-2 hover:text-foreground"
													>
														Tela de permissão OAuth
													</a>
													, escolha o tipo <strong>Externo</strong> e inclua a sua
													conta em <strong>Usuários de teste</strong>. Sem isso o
													Google bloqueia a autorização.
												</li>
												<li>
													Em{" "}
													<a
														href="https://console.cloud.google.com/apis/credentials"
														target="_blank"
														rel="noreferrer"
														className="underline underline-offset-2 hover:text-foreground"
													>
														APIs e serviços › Credenciais
													</a>
													, clique em{" "}
													<strong>Criar credenciais › ID do cliente OAuth</strong>
													, tipo{" "}
													<strong className="font-medium text-foreground">
														Aplicativo da Web
													</strong>
													. Em <em>URIs de redirecionamento autorizados</em>, informe o
													endereço do próximo passo.
												</li>
												<li>
													Copie o <strong>Client ID</strong> e o{" "}
													<strong>Client secret</strong> gerados e cole nos campos
													acima.
												</li>
												<li>
													Para o <strong>Refresh token</strong>, abra o{" "}
													<a
														href="https://developers.google.com/oauthplayground/"
														target="_blank"
														rel="noreferrer"
														className="underline underline-offset-2 hover:text-foreground"
													>
														OAuth 2.0 Playground
													</a>
													. Clique na engrenagem, marque{" "}
													<em>Use your own OAuth credentials</em> e informe os mesmos
													client id e secret. Em <em>Step 1</em>, selecione o escopo{" "}
													<code className="break-all">
														https://www.googleapis.com/auth/calendar
													</code>{" "}
													(não esqueça de informar{" "}
													<code className="break-all">
														https://developers.google.com/oauthplayground
													</code>{" "}
													no passo anterior), clique em{" "}
													<strong>Authorize APIs</strong> e depois em{" "}
													<strong>Exchange authorization code for tokens</strong>. Copie
													o <strong>Refresh token</strong> e cole no campo acima.
												</li>
											</ol>
											<p className="text-xs leading-relaxed text-muted-foreground">
												Esse escopo permite criar e ler eventos. Se preferir dar
												menos acesso, use{" "}
												<code>https://www.googleapis.com/auth/calendar.events</code>{" "}
												para os eventos e{" "}
												<code>https://www.googleapis.com/auth/calendar.readonly</code>{" "}
												para a consulta de disponibilidade.
											</p>
											<p className="text-xs leading-relaxed text-muted-foreground">
												As três credenciais precisam ser do{" "}
												<strong className="font-medium text-foreground">
													mesmo app OAuth
												</strong>
												. Client id de um app com refresh token de outro não
												funciona, e o erro que aparece é o do Google, não do
												sistema.
											</p>
											<DialogFooter>
												<Button
													type="button"
													onClick={() => setTutorialGoogleCalendar(false)}
												>
													Entendi
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>

									<div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
										<div>
											<div className="text-sm font-medium">Status da conexão</div>
											<div className="text-xs text-muted-foreground">
												O sistema precisa de client id, client secret e refresh token
												da mesma conta OAuth.
											</div>
										</div>
										{googleCalendar.pronto ? (
											<Badge variant="secondary" className="font-normal">
												<Check className="mr-1 h-3 w-3 text-emerald-600" />
												Conectado
											</Badge>
										) : (
											<Badge variant="outline" className="font-normal">
												<AlertTriangle className="mr-1 h-3 w-3 text-amber-600" />
												Sem conexão
											</Badge>
										)}
									</div>

									<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
										<div className="space-y-1.5 sm:col-span-2">
											<Label htmlFor="google-calendar-client-id">Client ID</Label>
											<Input
												id="google-calendar-client-id"
												value={googleCalendar.clientId}
												onChange={(e) =>
													setGoogleCalendar((prev) => ({
														...prev,
														clientId: e.target.value,
													}))
												}
												placeholder="App OAuth do Google Cloud"
											/>
											<p className="text-xs leading-relaxed text-muted-foreground">
												Credencial OAuth do Google Cloud com permissão de Google
												Calendar.
											</p>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="google-calendar-client-secret">
												Client secret
											</Label>
											<Input
												id="google-calendar-client-secret"
												type="password"
												autoComplete="new-password"
												value={novoGoogleCalendarSecret}
												onChange={(e) =>
													setNovoGoogleCalendarSecret(e.target.value)
												}
												placeholder={
													googleCalendar.clientSecret || "Cole um novo client secret"
												}
											/>
											<p className="text-xs leading-relaxed text-muted-foreground">
												{googleCalendar.clientSecret
													? `Guardado: ${googleCalendar.clientSecret}. Deixe em branco para manter.`
													: "Cole o segredo do mesmo app OAuth."}
											</p>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="google-calendar-refresh-token">
												Refresh token
											</Label>
											<Input
												id="google-calendar-refresh-token"
												type="password"
												autoComplete="new-password"
												value={novoGoogleCalendarRefresh}
												onChange={(e) =>
													setNovoGoogleCalendarRefresh(e.target.value)
												}
												placeholder={
													googleCalendar.refreshToken ||
													"Cole um novo refresh token"
												}
											/>
											<p className="text-xs leading-relaxed text-muted-foreground">
												{googleCalendar.refreshToken
													? `Guardado: ${googleCalendar.refreshToken}. Deixe em branco para manter.`
													: "Sem ele o servidor não consegue renovar o acesso sozinho."}
											</p>
										</div>

										<div className="space-y-1.5 sm:col-span-2">
											<Label htmlFor="google-calendar-default-id">
												Calendário padrão
											</Label>
											<Input
												id="google-calendar-default-id"
												value={googleCalendar.defaultCalendarId}
												onChange={(e) =>
													setGoogleCalendar((prev) => ({
														...prev,
														defaultCalendarId: e.target.value,
													}))
												}
												placeholder="primary"
											/>
											<p className="text-xs leading-relaxed text-muted-foreground">
												Use <code>primary</code> para a agenda principal, ou o e-mail/ID
												do calendário compartilhado.
											</p>
										</div>
									</div>
								</div>
							</SettingsCard>
						</>
					)}

					{/* ---------------- Meta ---------------- */}
					{activeSection === "meta" && (
						<SettingsCard
							title="App da Meta"
							description="Preencha com os dados do app criado em developers.facebook.com. Sem eles, os botões de conectar Facebook, Instagram e WhatsApp oficial não aparecem na tela de Conexões."
						>
							<MetaSettings />
						</SettingsCard>
					)}

					{/* ---------------- Backup ---------------- */}
					{activeSection === "backup" && <BackupRestore />}
				</div>
			</div>
		</div>
	);
};

export default Settings;
